import React, { useState, useEffect, useRef } from "react";
import { useRecoilValue, useRecoilState } from "recoil";
import { useNavigate } from "react-router-dom";
import { zoomSettingsState } from "../../../../recoil/zoomSettings";
import { userState } from "../../../../recoil/auth";
import { useAuth } from "../../../../hooks/useAuth";
import {
  missionServiceState,
  missionActionState,
  pastOrderState,
  addAdvertisementLoadingState,
  addAdvertisementErrorState,
} from "../../../../recoil/addAdvertisement";
import {
  getMissionServiceAPI,
  getMissionActionAPI,
  getMissionTemplateAPI,
  getPastOrderAPI,
  getKeywordNameAPI,
  getNaverSmartStoreAPI,
  getNaverShoppingRankAPI,
  getNaverPlayStoreAPI,
  getNaverPlayStoreRankAPI,
  getAppStoreAPI,
  getUserDetailAPI,
  createMissionAPI,
  createAdminNotificationAPI,
  getOriginalInfoMissionAPI,
  getRouteInfoAPI,
  getMemberAdAPI,
  getAllMemberAdAPI,
  getNaverShoppingReview,
  getCoupangProductRankAPI,
  getCoupangProductInfoAPI,
} from "../../../../api";
import { isSpecialUser } from "../../../../utils/environment";
import LoadingOverlay from "../../../../components/common/LoadingOverlay";
import advertismentIconActive from "../../../../assets/icons/advertismentIconActive.svg";
import { COLORS } from "../../../../constants";
import "./AddAdvertisement.css";

// 액션별 폼 컴포넌트들

// 페이지별 컴포넌트들
import ShoppingVisitPage from "./pages/ShoppingVisitPage";
import ShoppingVisitPageWithOrigin from "./pages/ShoppingVisitPageWithOrigin";
import PlaceVisitPage from "./pages/PlaceVisitPage";
import PlaceSavePage from "./pages/PlaceSavePage";
import AppReviewPage from "./pages/AppReviewPage";
import AppReviewIosPage from "./pages/AppReviewIosPage";
import CoupangVisitPage from "./pages/CoupangVisitPage";

const AddAdvertisement = ({ onTabChange }) => {
  const navigate = useNavigate();
  const user = useRecoilValue(userState);
  const { updateUserInfo } = useAuth();
  const [selectedMainTab, setSelectedMainTab] = useState("naver");
  const [selectedSubTab, setSelectedSubTab] = useState("shopping");
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [keywordRanks, setKeywordRanks] = useState([]);
  const [keywordLoadingIndex, setKeywordLoadingIndex] = useState(null);
  const [playStoreRankPromise, setPlayStoreRankPromise] = useState(null); // 백그라운드 API 완료 Promise
  const [originalInfoPromise, setOriginalInfoPromise] = useState(null); // 원부 정보 API 완료 Promise
  const [coupangRankPromise, setCoupangRankPromise] = useState(null); // 쿠팡 순위 API 백그라운드 Promise
  const [coupangProductInfoPromise, setCoupangProductInfoPromise] = useState(null); // 쿠팡 상품정보 API 백그라운드 Promise
  const coupangFetchTimeoutRef = useRef(null);
  const coupangLastFetchedRef = useRef({ url: "", keyword: "" });
  const [showConfirmModal, setShowConfirmModal] = useState(false); // 확인 모달 표시 상태

  // 리코일 상태
  const [missionServices, setMissionServices] =
    useRecoilState(missionServiceState);
  const [missionActions, setMissionActions] =
    useRecoilState(missionActionState);
  const [pastOrders, setPastOrders] = useRecoilState(pastOrderState);
  const [error, setError] = useRecoilState(addAdvertisementErrorState);

  // zoomSettings에서 주문 마감 시간 가져오기
  const zoomSettings = useRecoilValue(zoomSettingsState);
  const endSubmitTime = zoomSettings?.end_submit || "17:00";

  // 광고 시작일 최소 날짜 계산
  const getMinStartDate = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // endSubmitTime 파싱 (예: "17:00" -> 17, 0)
    const [endHour, endMinute] = endSubmitTime.split(":").map(Number);

    // 현재 시간을 분으로 변환
    const currentTimeInMinutes = currentHour * 60 + currentMinute;
    const endTimeInMinutes = endHour * 60 + endMinute;

    // 오늘 날짜 기준으로 계산
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 현재 시간이 endSubmitTime 이후인지 확인
    if (currentTimeInMinutes >= endTimeInMinutes) {
      // endSubmitTime 이후면 내일 모레부터 (2일 후)
      const minDate = new Date(today);
      minDate.setDate(today.getDate() + 2);
      return minDate.toISOString().split("T")[0];
    } else {
      // endSubmitTime 이전이면 내일부터 (1일 후)
      const minDate = new Date(today);
      minDate.setDate(today.getDate() + 1);
      return minDate.toISOString().split("T")[0];
    }
  };

  const [formData, setFormData] = useState({
    // 중복 요청 레디스 설정 키
    idempotencyKey: "",

    // 1차 분류 이름
    serviceName: "",

    // 2차 분류 이름
    actionName: "",

    // 3차 분류 이름 (광고 이름)
    missionName: "",

    // missionAction.ma_id
    missionType: 0,

    // missionTemplate.mt_id
    missionTemplate: 0,

    // 일단 appPush 고정
    openType: "appPush",

    // 일단 openWebSite 고정
    openWebSite: "naver",

    // rewardSilver
    rewardSilver: 0,

    // 웹 회원 ID (mongoose-long 필요)
    webMemberId: user?.id || 0,

    // 광고 상품 이름 (ma id 1,2 "정답" 3,4 "주변,명소" 5 "스토어")
    productName: "",

    // 1,2 인 쇼핑만 있음
    storeName: "",

    // 상품 URL
    productUrl: "",

    // 외부 시스템의 상품 ID (productUrl에서 추출)
    productId: 0,

    // 원본 식별 ID (nullable)
    originId: "",

    // 이미지 주소
    imageUrl: "",

    // 채널 UID (네이버 스마트스토어)
    channelUid: "",

    // status
    status: "PENDING",

    // memo
    memo: "",

    // 5번 리뷰 "" 나버지는 productId
    approveAnswer: "",

    // 체류 시간
    mustRemainingTime: 0,

    // detail (0 => 앱리뷰, 1 => 플레이스(api), 2 => 쇼핑(크롤링))
    detail: 0,

    // 미션 중에 하루 미션 완료 한도 있는 키
    needUpdata: false,

    // 웹 회원의 총 금액
    price: 0,

    // 1회당 단가
    userPriceByOne: 0,

    // 총 수량 (광고 기간 × 일일 수량으로 자동 계산됨)
    totalQuantity: "",

    // 일일 수량 제한
    dailyQuantity: "",

    // 오늘 미션 완료 수량
    todayUseCount: 0,

    // 대기
    todayWaitingCount: 0,

    // 남은 수량
    todayRemainingCount: 0,

    // 광고 시작 날짜
    startDate: "",

    // 광고 종료 날짜
    endDate: "",

    // 광고 종료일 (UI용)
    adEndDate: "",

    // 메인 키워드 정보
    mainKeyword: {
      name: "",
      rank: 999,
    },

    // // 서브 키워드 배열
    subKeywords: [],

    // 기존 필드들 (UI용)
    // categoryUrl: "",
    placeName: "",
    placeUrl: "",
    appName: "",
    googlePlayUrl: "",
    appStoreUrl: "",
    adDuration: "",
    adStartDate: "",
    applicationQuantity: "",
    pastOrder: "",
    priceComparison: false,
  });

  // 키워드 순위 리스트 관리 함수들
  const addKeywordRank = () => {
    setKeywordRanks((prev) => [
      ...prev,
      { keyword: "", rank: 0, selected: true },
    ]);
  };

  const removeKeywordRank = (actualIndex) => {
    setKeywordRanks((prev) => {
      return prev.map((item, index) =>
        index === actualIndex ? { ...item, selected: false } : item
      );
    });
  };

  const updateKeywordRank = (index, field, value) => {
    setKeywordRanks((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  // URL에서 productId 추출하는 함수
  const extractProductIdFromUrl = (url) => {
    if (!url) return null;

    // 입력값을 문자열로 변환
    const urlString = url.toString();

    // @로 시작하는 경우 제거
    let cleanUrl = urlString.startsWith("@")
      ? urlString.substring(1)
      : urlString;

    // ? 뒤의 쿼리 파라미터 제거
    cleanUrl = cleanUrl.split("?")[0];

    // 네이버 플레이스 URL 패턴 확인 (PC)
    const placePattern = /\/entry\/place\/(\d+)/;
    const placeMatch = cleanUrl.match(placePattern);
    if (placeMatch) {
      return placeMatch[1];
    }

    // 더 포괄적인 네이버 플레이스 패턴 (entry/place/숫자)
    const generalPlacePattern = /entry\/place\/(\d+)/;
    const generalPlaceMatch = cleanUrl.match(generalPlacePattern);
    if (generalPlaceMatch) {
      return generalPlaceMatch[1];
    }

    // 네이버 플레이스 URL 패턴 확인 (모바일)
    const mobilePlacePattern = /\/restaurant\/(\d+)\/home/;
    const mobilePlaceMatch = cleanUrl.match(mobilePlacePattern);
    if (mobilePlaceMatch) {
      return mobilePlaceMatch[1];
    }

    // 네이버 스마트스토어 URL 패턴 확인
    const shoppingPattern = /\/products\/(\d+)/;
    const shoppingMatch = cleanUrl.match(shoppingPattern);
    if (shoppingMatch) {
      return shoppingMatch[1];
    }

    // 기존 방식: URL에서 뒤에서부터 숫자 추출 (productId는 보통 URL 끝 부분에 위치)
    const numberMatches = cleanUrl.match(/(\d+)/g);
    if (numberMatches && numberMatches.length > 0) {
      // 뒤에서부터 가장 긴 숫자를 반환
      return numberMatches
        .reverse()
        .reduce((longest, current) =>
          current.length > longest.length ? current : longest
        );
    }

    return null;
  };

  // HTML 엔티티를 디코딩하는 함수
  const decodeHtmlEntities = (text) => {
    if (!text) return text;

    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  };

  const handleInputChange = async (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]:
        field === "mainKeyword"
          ? { name: value, rank: 999 }
          : field === "dailyQuantity"
          ? parseInt(value) || 0
          : value,
    }));

    // productUrl 입력 시 productId 자동 추출 (selectedSubTab이 1, 2, 3, 4인 경우)
    if (
      field === "productUrl" &&
      (selectedSubTab === 1 ||
        selectedSubTab === 2 ||
        selectedSubTab === 3 ||
        selectedSubTab === 4)
    ) {
      const extractedProductId = extractProductIdFromUrl(value);
      if (extractedProductId) {
        setFormData((prev) => ({
          ...prev,
          productId: parseInt(extractedProductId, 10),
          approveAnswer: extractedProductId,
        }));
      }
    }

    // originId 입력 시 productId와 approveAnswer 자동 추출 (selectedSubTab이 2인 경우)
    if (field === "originId" && selectedSubTab === 2) {
      const extractedProductId = extractProductIdFromUrl(value);
      if (extractedProductId) {
        setFormData((prev) => ({
          ...prev,
          productId: parseInt(extractedProductId, 10),
          approveAnswer: extractedProductId,
        }));
      }
    }

    // 과거 주문 선택 시 키워드 정보 불러와서 필드 채우기
    if (field === "pastOrder" && value) {
      try {
        const selectedOrder = pastOrders.find(
          (order) => order.ad_id === parseInt(value)
        );
        if (selectedOrder) {
          // 키워드 정보 불러오기
          const keywordResponse = await getKeywordNameAPI(selectedOrder.ad_id);
          const keywords = keywordResponse.data?.data || [];

          // 메인 키워드와 서브 키워드 분리
          const mainKeyword = keywords.find((k) => k.type === "MAIN");
          const subKeywords = keywords.filter((k) => k.type === "SUB");

          // 과거 주문 데이터로 formData 업데이트 (기존 formData를 기반으로 업데이트)
          const updatedFormData = {
            ...formData, // 기존 formData의 모든 필드 유지

            // 서비스 정보 (현재 선택된 서비스와 액션 정보로 설정)
            serviceName:
              missionServices.find((service) => service.id === selectedMainTab)
                ?.originalName || "",
            // actionName은 getMissionTemplateAPI에서 설정되므로 여기서 설정하지 않음
            missionName: selectedOrder.missionName || "",
            missionType:
              missionServices.find((service) => service.id === selectedMainTab)
                ?.ms_id || 0,
            missionTemplate:
              missionActions.find((action) => action.id === selectedSubTab)
                ?.ma_id || 0,
            rewardSilver: selectedOrder.rewardSilver || 0,

            // 과거 주문에서 가져올 데이터
            productName: selectedOrder.productName || "",
            storeName: selectedOrder.storeName || "",
            productUrl: selectedOrder.productUrl || "",
            productId: selectedOrder.productId || "",
            imageUrl: selectedOrder.imageUrl || "",
            memo: selectedOrder.memo || "",
            dailyQuantity: selectedOrder.dailyQuantity || 0,
            totalQuantity: selectedOrder.totalQuantity || 0,
            approveAnswer: selectedOrder.approveAnswer || "",
            mustRemainingTime: selectedOrder.mustRemainingTime || 0,
            detail: selectedOrder.detail || 0,
            needUpdata: selectedOrder.needUpdata || false,
            todayRemainingCount: selectedOrder.dailyQuantity || 0,

            // 키워드 정보
            mainKeyword: {
              name: mainKeyword?.name || "",
              rank: 999,
            },
            subKeywords: subKeywords.map((keyword) => ({ name: keyword.name })),

            // 과거 주문 선택 상태
            pastOrder: value,
          };

          // 현재 선택된 서브 탭에 따라 특정 필드들 추가 설정
          if (selectedSubTab === 2) {
            // ShoppingVisitPageWithOrigin (원산지 포함)
            updatedFormData.originId = selectedOrder.originId || "";
          } else if (selectedSubTab === 3) {
            // PlaceVisitPage (플레이스 방문)
            updatedFormData.placeUrl = selectedOrder.productUrl || "";
          } else if (selectedSubTab === 4) {
            // PlaceSavePage (플레이스 저장)
            updatedFormData.placeUrl = selectedOrder.productUrl || "";
          } else if (selectedSubTab === 5) {
            // AppReviewPage (앱 리뷰)
            if (selectedOrder.productUrl) {
              let cleanUrl = selectedOrder.productUrl;
              if (cleanUrl.startsWith("@")) {
                cleanUrl = cleanUrl.substring(1);
              }

              const urls = cleanUrl.split(":::");
              let googlePlayUrl = "";
              let appStoreUrl = "";

              urls.forEach((url) => {
                const trimmedUrl = url.trim();
                if (trimmedUrl.includes("play.google.com")) {
                  googlePlayUrl = trimmedUrl;
                } else if (trimmedUrl.includes("apps.apple.com")) {
                  appStoreUrl = trimmedUrl;
                }
              });

              updatedFormData.googlePlayUrl = googlePlayUrl;
              updatedFormData.appStoreUrl = appStoreUrl;
            }
          } else if (selectedSubTab === 6) {
            // AppReviewIosPage (iOS 앱 리뷰)
            if (selectedOrder.productUrl) {
              let cleanUrl = selectedOrder.productUrl;
              if (cleanUrl.startsWith("@")) {
                cleanUrl = cleanUrl.substring(1);
              }

              const urls = cleanUrl.split(":::");
              let googlePlayUrl = "";
              let appStoreUrl = "";

              urls.forEach((url) => {
                const trimmedUrl = url.trim();
                if (trimmedUrl.includes("play.google.com")) {
                  googlePlayUrl = trimmedUrl;
                } else if (trimmedUrl.includes("apps.apple.com")) {
                  appStoreUrl = trimmedUrl;
                }
              });

              updatedFormData.googlePlayUrl = googlePlayUrl;
              updatedFormData.appStoreUrl = appStoreUrl;
            }
          }

          setFormData(updatedFormData);

          // keywordRanks 업데이트 (subKeywords를 selected: true로 설정)
          const keywordRanksFromPastOrder = subKeywords.map((keyword) => ({
            keyword: keyword.name,
            rank: 999, // 과거 주문에서는 순위 정보가 없으므로 999로 설정
            selected: true, // 과거 주문의 키워드는 모두 선택된 상태로 설정
          }));
          setKeywordRanks(keywordRanksFromPastOrder);
        }
      } catch (error) {
        console.error("키워드 정보 조회 실패:", error);
      }
    }
  };

  // 미션 서비스 목록 가져오기
  const loadMissionServices = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getMissionServiceAPI();

      if (response.data?.data) {
        const services = response.data.data.map((service) => ({
          id: service.ms_id,
          name: service.name + " 광고",
          originalName: service.name,
          ms_id: service.ms_id,
        }));
        setMissionServices(services);

        // 첫 번째 서비스 선택 및 액션 목록 가져오기
        if (services.length > 0) {
          const firstService = services[0];
          setSelectedMainTab(firstService.id); // 첫 번째 서비스 선택

          // 첫 번째 서비스의 serviceName 설정
          setFormData((prev) => ({
            ...prev,
            serviceName: firstService.originalName,
            missionType: firstService.ms_id,
          }));

          await loadMissionActions(firstService.ms_id);
        }
      }
    } catch (err) {
      console.error("미션 서비스 로딩 실패:", err);
      setError("미션 서비스 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 미션 액션 목록 가져오기
  const loadMissionActions = async (ms_id) => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getMissionActionAPI(ms_id);

      if (response.data?.data) {
        const actions = response.data.data
          .map((action) => ({
            id: action.ma_id,
            name: action.name,
            ma_id: action.ma_id,
          }))
          .sort((a, b) => a.ma_id - b.ma_id); // ma_id 순으로 정렬
        setMissionActions(actions);

        // 첫 번째 액션 선택
        if (actions.length > 0) {
          const firstAction = actions[0];
          setSelectedSubTab(firstAction.id); // 첫 번째 액션 선택

          // 첫 번째 액션에 대한 userPriceByOne 설정
          if (
            user?.price_grade_detail?.data &&
            Array.isArray(user?.price_grade_detail?.data)
          ) {
            const priceDetail = user?.price_grade_detail?.data.find(
              (detail) => detail.mission_template_id === firstAction.id
            );

            if (priceDetail) {
              setFormData((prev) => ({
                ...prev,
                userPriceByOne: priceDetail.price,
              }));
            }
          }

          // 선택된 서비스와 액션으로 미션 템플릿 정보 가져오기
          try {
            const templateResponse = await getMissionTemplateAPI(
              ms_id,
              firstAction.ma_id
            );

            const templateData = templateResponse?.data?.data;
            if (Array.isArray(templateData) && templateData.length > 0) {
              // 선택한 ma_id와 일치하는 missionTemplate을 가진 템플릿 찾기
              const template = templateData.find(
                (t) => t.missionTemplate === firstAction.ma_id
              );

              if (template) {
                // ma_id에 따른 productName 설정
                let productName = "";
                if (firstAction.ma_id === 1 || firstAction.ma_id === 2) {
                  // productName = "정답";
                  productName = "태그";
                } else if (firstAction.ma_id === 3) {
                  // productName = "주변,명소";
                  // productName = "지도,주차장";
                  productName = "길찾기";
                } else if (firstAction.ma_id === 4) {
                  productName = "저장";
                } else if (firstAction.ma_id === 5) {
                  productName = "스토어";
                }

                setFormData((prev) => ({
                  ...prev,
                  actionName: template.name,
                  openType: template.defaultOpenType,
                  openWebSite: template.defaultOpenWebSite,
                  rewardSilver: template.rewardSilver,
                  missionTemplate: template.mt_id,
                  productName: productName,
                }));
              } else {
                console.warn(
                  `ma_id ${firstAction.ma_id}와 일치하는 템플릿을 찾을 수 없습니다.`
                );
              }
            }
          } catch (error) {
            console.error("첫 번째 액션 미션 템플릿 로딩 실패:", error);
          }

          // 과거 주문은 드롭박스 클릭 시에만 로드하도록 변경
          // await loadPastOrders(ms_id, firstAction.ma_id);
        }
      }
    } catch (err) {
      console.error("미션 액션 로딩 실패:", err);
      setError("미션 액션 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 과거 주문 목록 가져오기
  const loadPastOrders = async (
    missionType,
    missionTemplate,
    showLoading = false
  ) => {
    if (!user?.id || !missionType || !missionTemplate) return;

    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const response = await getPastOrderAPI(
        user.id,
        missionType,
        missionTemplate
      );

      if (response.data?.data && Array.isArray(response.data.data)) {
        // missionName별로 그룹화하고 createdAt이 가장 최근인 것만 선택
        const groupedOrders = response.data.data.reduce((acc, order) => {
          const missionName = order.missionName;

          if (
            !acc[missionName] ||
            new Date(order.createdAt) > new Date(acc[missionName].createdAt)
          ) {
            acc[missionName] = order;
          }

          return acc;
        }, {});

        // 그룹화된 결과를 배열로 변환
        const uniqueOrders = Object.values(groupedOrders).map((order) => ({
          name: order.missionName,
          ad_id: order.ad_id,
          ...order,
        }));

        setPastOrders(uniqueOrders);
      }
    } catch (err) {
      console.error("과거 주문 로딩 실패:", err);
      setError("과거 주문 목록을 불러오는데 실패했습니다.");
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // 메인 탭 변경 시
  const handleMainTabChange = async (tabId) => {
    setSelectedMainTab(tabId);

    // 탭 변경 시 모든 관련 필드들 초기화
    setFormData((prev) => ({
      ...prev,
      // 기본 정보
      missionName: "",
      storeName: "",
      productName: "",
      
      // URL 관련
      productUrl: "",
      originId: "",
      placeUrl: "",
      googlePlayUrl: "",
      appStoreUrl: "",
      
      // 키워드 관련
      mainKeyword: {
        name: "",
        rank: 999,
      },
      subKeywords: [],
      
      // 수량 및 기간
      dailyQuantity: "",
      adDuration: "",
      adStartDate: "",
      adEndDate: "",
      totalQuantity: "",
      
      // 가격 관련
      price: 0,
      userPriceByOne: 0,
      
      // 기타
      pastOrder: "",
      applicationQuantity: "",
      placeName: "",
      appName: "",
      imageUrl: "",
      channelUid: "",
      approveAnswer: "",
      mustRemainingTime: 0,
      priceComparison: false,
      
      // 날짜
      startDate: "",
      endDate: "",
      
      // 통계
      todayUseCount: 0,
      todayWaitingCount: 0,
      todayRemainingCount: 0,
      
      // 메모 초기화
      memo: "",
      
      // 기본값 유지
      openType: "appPush",
      openWebSite: "naver",
    }));

    // keywordRanks 초기화
    setKeywordRanks([]);

    // 선택된 서비스의 ms_id 찾기
    const selectedService = missionServices.find(
      (service) => service.id === tabId
    );
    if (selectedService) {
      // 선택된 서비스의 serviceName 설정
      setFormData((prev) => ({
        ...prev,
        serviceName: selectedService.originalName,
        missionType: selectedService.ms_id,
      }));

      await loadMissionActions(selectedService.ms_id);
      // 서브 탭은 loadMissionActions에서 첫 번째 액션으로 자동 설정됨
    }
  };

  // 서브 탭 변경 시
  const handleSubTabChange = async (tabId) => {
    setSelectedSubTab(tabId);

    // 탭 변경 시 관련 필드들 초기화 (기본값 유지)
    setFormData((prev) => ({
      ...prev,
      // 기본 정보
      missionName: "",
      productName: "",
      storeName: "",

      // URL 관련
      productUrl: "",
      originId: "",
      placeUrl: "",
      googlePlayUrl: "",
      appStoreUrl: "",

      // 키워드 관련
      mainKeyword: {
        name: "",
        rank: 999,
      },
      subKeywords: [],

      // 수량 및 기간
      dailyQuantity: 0,
      adDuration: "",
      adStartDate: "",
      adEndDate: "",
      totalQuantity: 0,

      // 가격 관련
      price: 0,

      // 기타
      pastOrder: "",
      applicationQuantity: "",
      placeName: "",
      appName: "",
      imageUrl: "",
      channelUid: "",
      approveAnswer: "",
      mustRemainingTime: 0,
      priceComparison: false,

      // 날짜
      startDate: "",
      endDate: "",

      // 통계
      todayUseCount: 0,
      todayWaitingCount: 0,
      todayRemainingCount: 0,

      // 메모 초기화
      memo: "",

      // 기본값 유지 (API 응답 대기 중)
      openType: "appPush",
      openWebSite: "naver",
    }));

    if (
      user?.price_grade_detail?.data &&
      Array.isArray(user?.price_grade_detail?.data)
    ) {
      const priceDetail = user?.price_grade_detail?.data.find(
        (detail) => detail.mission_template_id === parseInt(tabId)
      );

      if (priceDetail) {
        setFormData((prev) => {
          const updated = {
            ...prev,
            userPriceByOne: priceDetail.price,
          };
          return updated;
        });
      }
    }

    // 선택된 서비스와 액션의 ID 찾기
    const selectedService = missionServices.find(
      (service) => service.id === selectedMainTab
    );
    const selectedAction = missionActions.find((action) => action.id === tabId);

    if (selectedService && selectedAction) {
      // 선택한 missionType(ms_id)과 missionTemplate(ma_id)로 미션 템플릿 정보 가져오기
      try {
        const templateResponse = await getMissionTemplateAPI(
          selectedService.ms_id,
          selectedAction.ma_id
        );

        // 미션 템플릿 데이터가 있으면 formData 업데이트
        const templateData = templateResponse?.data?.data;
        if (Array.isArray(templateData) && templateData.length > 0) {
          // 선택한 ma_id와 일치하는 missionTemplate을 가진 템플릿 찾기
          const template = templateData.find(
            (t) => t.missionTemplate === selectedAction.ma_id
          );

          if (template) {
            // ma_id에 따른 productName 설정
            let productName = "";
            if (selectedAction.ma_id === 1 || selectedAction.ma_id === 2) {
              // productName = "정답";
              productName = "태그";
            } else if (selectedAction.ma_id === 3) {
              // productName = "주변,명소";
              // productName = "지도,주차장";
              productName = "길찾기";
            } else if (selectedAction.ma_id === 4) {
              productName = "저장";
            } else if (selectedAction.ma_id === 5) {
              productName = "스토어";
            } else if (selectedAction.ma_id === 13) {
              productName = "정답";
            }

            setFormData((prev) => ({
              ...prev,
              actionName: template.name,
              openType: template.defaultOpenType,
              openWebSite: template.defaultOpenWebSite,
              rewardSilver: template.rewardSilver,
              missionTemplate: template.mt_id,
              productName: productName,
            }));
          } else {
            console.warn(
              `ma_id ${selectedAction.ma_id}와 일치하는 템플릿을 찾을 수 없습니다.`
            );
          }
        }
      } catch (error) {
        console.error("미션 템플릿 로딩 실패:", error);
      }

      // 과거 주문은 드롭박스 클릭 시에만 로드하도록 변경
      // await loadPastOrders(selectedService.ms_id, selectedAction.ma_id);
    }

    // keywordRanks 초기화
    setKeywordRanks([]);
  };

  // 컴포넌트 마운트 시 미션 서비스 로딩
  useEffect(() => {
    if (user?.id) {
      loadMissionServices();
    }
  }, [user?.id]);

  // user 상태 변경 시 webMemberId 업데이트 및 userPriceByOne 설정
  useEffect(() => {
    if (user?.id) {
      setFormData((prev) => ({
        ...prev,
        webMemberId: user.id,
      }));

      // 조건을 분리해서 확인
      const hasPriceGradeDetail = !!user?.price_grade_detail?.data;
      const isArray = Array.isArray(user?.price_grade_detail?.data);

      if (
        user?.price_grade_detail?.data &&
        Array.isArray(user?.price_grade_detail?.data)
      ) {
        const priceDetail = user?.price_grade_detail?.data.find(
          (detail) => detail.mission_template_id === parseInt(selectedSubTab)
        );

        if (priceDetail) {
          setFormData((prev) => ({
            ...prev,
            userPriceByOne: priceDetail.price,
          }));
        }
      }
    }
  }, [user?.id, user?.price_grade_detail?.data, selectedSubTab]);

  // userPriceByOne이 변경될 때 memo의 unitPrice 업데이트
  useEffect(() => {
    if (
      formData.userPriceByOne !== undefined &&
      formData.userPriceByOne !== null
    ) {
      setFormData((prev) => {
        try {
          const existingMemo = prev.memo ? JSON.parse(prev.memo) : {};
          const updatedMemo = JSON.stringify({
            ...existingMemo,
            unitPrice: String(formData.userPriceByOne || 0),
          });

          return {
            ...prev,
            memo: updatedMemo,
          };
        } catch (error) {
          console.error("memo 업데이트 중 JSON 파싱 오류:", error);
          return prev;
        }
      });
    }
  }, [formData.userPriceByOne]);

  // 쿠팡 탭(13): 상품 URL/메인키워드 입력 시 백그라운드로 순위·상품정보 API 호출 (제출 시 대기만 함)
  useEffect(() => {
    if (selectedSubTab !== 13) {
      setCoupangRankPromise(null);
      setCoupangProductInfoPromise(null);
      coupangLastFetchedRef.current = { url: "", keyword: "" };
      if (coupangFetchTimeoutRef.current) {
        clearTimeout(coupangFetchTimeoutRef.current);
        coupangFetchTimeoutRef.current = null;
      }
      return;
    }
    const productUrl = (formData.productUrl || "").trim();
    const mainKeywordName = (formData.mainKeyword?.name || "").trim();
    if (!productUrl) {
      setCoupangRankPromise(null);
      setCoupangProductInfoPromise(null);
      return;
    }
    const sameAsLast =
      coupangLastFetchedRef.current.url === productUrl &&
      coupangLastFetchedRef.current.keyword === mainKeywordName;
    if (sameAsLast) return;

    // URL/키워드가 바뀌었으면 이전 Promise는 버림 (제출 시 새로 호출하거나 새 fetch 완료 후 사용)
    setCoupangRankPromise(null);
    setCoupangProductInfoPromise(null);

    if (coupangFetchTimeoutRef.current) {
      clearTimeout(coupangFetchTimeoutRef.current);
      coupangFetchTimeoutRef.current = null;
    }
    coupangFetchTimeoutRef.current = setTimeout(() => {
      coupangFetchTimeoutRef.current = null;
      coupangLastFetchedRef.current = { url: productUrl, keyword: mainKeywordName };

      const rankPromise = getCoupangProductRankAPI(mainKeywordName, productUrl)
        .then((res) => res?.data?.organicRank ?? 999)
        .catch(() => 999);
      setCoupangRankPromise(rankPromise);

      const productInfoPromise = getCoupangProductInfoAPI(productUrl)
        .then((res) =>
          res?.data?.success && res?.data?.data ? res.data.data : null
        )
        .catch(() => null);
      setCoupangProductInfoPromise(productInfoPromise);
    }, 700);

    return () => {
      if (coupangFetchTimeoutRef.current) {
        clearTimeout(coupangFetchTimeoutRef.current);
        coupangFetchTimeoutRef.current = null;
      }
    };
  }, [selectedSubTab, formData.productUrl, formData.mainKeyword?.name]);

  // formData 상태 변경 시 로그 출력
  useEffect(() => {
    // console.log("📝 formData 변경됨:", {
    //   // 기본 정보
    //   serviceName: formData.serviceName,
    //   actionName: formData.actionName,
    //   missionName: formData.missionName,
    //   productName: formData.productName,

    //   // URL 정보
    //   productUrl: formData.productUrl,
    //   originId: formData.originId,

    //   // 날짜 정보
    //   adStartDate: formData.adStartDate,
    //   adEndDate: formData.adEndDate,
    //   adDuration: formData.adDuration,

    //   // 수량 정보
    //   dailyQuantity: formData.dailyQuantity,
    //   totalQuantity: formData.totalQuantity,

    //   // 가격 정보
    //   userPriceByOne: formData.userPriceByOne,
    //   price: formData.price,

    //   // 키워드 정보
    //   mainKeyword: formData.mainKeyword,

    //   // 기타
    //   memo: formData.memo,
    //   selectedSubTab: selectedSubTab,
    // });

    // 계산된 값들 표시
    const calculatedEndDate =
      formData.adStartDate && formData.adDuration
        ? new Date(
            new Date(formData.adStartDate).getTime() +
              ((parseInt(formData.adDuration) || 0) - 1) * 24 * 60 * 60 * 1000
          ).toISOString()
        : "";

    const calculatedTotalQuantity =
      (parseInt(formData.dailyQuantity) || 0) *
      (parseInt(formData.adDuration) || 0);

    // console.log("🧮 계산된 값들:", {
    //   calculatedEndDate,
    //   calculatedTotalQuantity,
    // });
  }, [formData, selectedSubTab]);

  const handleNext = async () => {
    // 로그인하지 않은 경우 로그인 화면으로 이동
    if (!user?.id) {
      navigate("/login?redirect=home&tab=addAdvertisement");
      return;
    }

    const validateMinimumRequirements = () => {
      const userIsSpecial = isSpecialUser(user?.id);

      if (userIsSpecial) {
        return true;
      }

      const dailyQuantity = parseInt(formData.dailyQuantity) || 0;

      // adDuration을 날짜로부터 직접 계산
      let adDuration = 0;
      if (formData.adStartDate && formData.adEndDate) {
        const startDate = new Date(formData.adStartDate);
        const endDate = new Date(formData.adEndDate);
        const diffTime = endDate - startDate;
        adDuration = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      }

      if (dailyQuantity < 100 || adDuration < 7) {
        alert(
          "최소 일일 수량은 100회, 최소 광고 기간은 7일입니다.\n다시 입력해주세요."
        );
        return false;
      }
      if (dailyQuantity % 100 !== 0) {
        alert("일일 수량은 100의 배수여야 합니다.");
        return false;
      }

      return true;
    };

    // selectedSubTab이 1일 때 필수 필드 검증
    if (selectedSubTab === 1) {
      const requiredFields = {
        productUrl: "상품 URL 주소",
        mainKeyword: "메인키워드(순위체크용)",
        dailyQuantity: "일일 수량",
        adStartDate: "광고 시작일",
        adEndDate: "광고 종료일",
      };

      const missingFields = [];

      for (const [field, label] of Object.entries(requiredFields)) {
        if (field === "mainKeyword") {
          // mainKeyword가 객체인지 문자열인지 확인
          const mainKeywordValue =
            typeof formData[field] === "object"
              ? formData[field]?.name
              : formData[field];
          if (!mainKeywordValue || mainKeywordValue.trim() === "") {
            missingFields.push(label);
          }
        } else {
          if (!formData[field] || formData[field].toString().trim() === "") {
            missingFields.push(label);
          }
        }
      }
      // 필수 필드 검증
      if (missingFields.length > 0) {
        alert(`다음 필드를 입력해주세요:\n${missingFields.join("\n")}`);
        return;
      }

      if (!validateMinimumRequirements()) {
        return;
      }

      // selectedSubTab이 2일 때 필수 필드 검증
    } else if (selectedSubTab === 2) {
      const requiredFields = {
        productUrl: "상품 URL 주소",
        mainKeyword: "메인키워드(순위체크용)",
        productName: "상품명",
        originId: "카테고리 상품 URL",
        dailyQuantity: "일일 수량",
        adStartDate: "광고 시작일",
        adEndDate: "광고 종료일",
      };

      const missingFields = [];

      for (const [field, label] of Object.entries(requiredFields)) {
        if (field === "mainKeyword") {
          // mainKeyword가 객체인지 문자열인지 확인
          const mainKeywordValue =
            typeof formData[field] === "object"
              ? formData[field]?.name
              : formData[field];
          if (!mainKeywordValue || mainKeywordValue.trim() === "") {
            missingFields.push(label);
          }
        } else {
          if (!formData[field] || formData[field].toString().trim() === "") {
            missingFields.push(label);
          }
        }
      }

      if (missingFields.length > 0) {
        alert(`다음 필드를 입력해주세요:\n${missingFields.join("\n")}`);
        return;
      }

      if (!validateMinimumRequirements()) {
        return;
      }

      // selectedSubTab이 3 또는 4일 때 필수 필드 검증
    } else if (selectedSubTab === 3 || selectedSubTab === 4) {
      const requiredFields = {
        productUrl: "플레이스 URL 주소",
        mainKeyword: "메인키워드(순위체크용)",
        dailyQuantity: "일일 수량",
        adStartDate: "광고 시작일",
        adEndDate: "광고 종료일",
      };

      const missingFields = [];

      for (const [field, label] of Object.entries(requiredFields)) {
        if (field === "mainKeyword") {
          // mainKeyword가 객체인지 문자열인지 확인
          const mainKeywordValue =
            typeof formData[field] === "object"
              ? formData[field]?.name
              : formData[field];
          if (!mainKeywordValue || mainKeywordValue.trim() === "") {
            missingFields.push(label);
          }
        } else {
          if (!formData[field] || formData[field].toString().trim() === "") {
            missingFields.push(label);
          }
        }
      }

      if (missingFields.length > 0) {
        alert(`다음 필드를 입력해주세요:\n${missingFields.join("\n")}`);
        return;
      }

      if (!validateMinimumRequirements()) {
        return;
      }

      // selectedSubTab이 5 나 6일 때 필수 필드 검증 selectedSubTab === 5 나 6
    } else if (selectedSubTab === 5) {
      // selectedSubTab이 5 나 6 일 때 필수 필드 검증
      const requiredFields = {
        googlePlayUrl: "구글 플레이스토어 URL 주소",
        // appStoreUrl: "앱스토어 URL 주소",
        mainKeyword: "메인키워드",
        totalQuantity: "신청 수량(총 수량)",
        adStartDate: "광고 시작일",
        adEndDate: "광고 종료일",
      };

      const missingFields = [];

      for (const [field, label] of Object.entries(requiredFields)) {
        if (field === "mainKeyword") {
          // mainKeyword가 객체인지 문자열인지 확인
          const mainKeywordValue =
            typeof formData[field] === "object"
              ? formData[field]?.name
              : formData[field];
          if (!mainKeywordValue || mainKeywordValue.trim() === "") {
            missingFields.push(label);
          }
        } else {
          if (!formData[field] || formData[field].toString().trim() === "") {
            missingFields.push(label);
          }
        }
      }

      if (missingFields.length > 0) {
        alert(`다음 필드를 입력해주세요:\n${missingFields.join("\n")}`);
        return;
      }
         } else if (selectedSubTab === 13) {
      const requiredFields = {
        missionName: "상품명",
        productUrl: "상품 URL 주소",
        mainKeyword: "메인키워드(순위체크용)",
        dailyQuantity: "일일 수량",
        adStartDate: "광고 시작일",
        adEndDate: "광고 종료일",
      };

      const missingFields = [];

      for (const [field, label] of Object.entries(requiredFields)) {
        if (field === "mainKeyword") {
          // mainKeyword가 객체인지 문자열인지 확인
          const mainKeywordValue =
            typeof formData[field] === "object"
              ? formData[field]?.name
              : formData[field];
          if (!mainKeywordValue || mainKeywordValue.trim() === "") {
            missingFields.push(label);
          }
        } else {
          if (!formData[field] || formData[field].toString().trim() === "") {
            missingFields.push(label);
          }
        }
      }
      // 필수 필드 검증
      if (missingFields.length > 0) {
        alert(`다음 필드를 입력해주세요:\n${missingFields.join("\n")}`);
        return;
      }

      if (!validateMinimumRequirements()) {
        return;
      }
    } else {
      // selectedSubTab이 5 나 6 일 때 필수 필드 검증
      const requiredFields = {
        appStoreUrl: "앱스토어 URL 주소",
        // appStoreUrl: "앱스토어 URL 주소",
        mainKeyword: "메인키워드",
        totalQuantity: "신청 수량(총 수량)",
        adStartDate: "광고 시작일",
        adEndDate: "광고 종료일",
      };

      const missingFields = [];

      for (const [field, label] of Object.entries(requiredFields)) {
        if (field === "mainKeyword") {
          // mainKeyword가 객체인지 문자열인지 확인
          const mainKeywordValue =
            typeof formData[field] === "object"
              ? formData[field]?.name
              : formData[field];
          if (!mainKeywordValue || mainKeywordValue.trim() === "") {
            missingFields.push(label);
          }
        } else {
          if (!formData[field] || formData[field].toString().trim() === "") {
            missingFields.push(label);
          }
        }
      }

      if (missingFields.length > 0) {
        alert(`다음 필드를 입력해주세요:\n${missingFields.join("\n")}`);
        return;
      }
    }

    // 로딩 상태 시작
    setLoading(true);

    // 회원 예치금 검증
    try {
      // 회원 정보 업데이트
      const updateResult = await updateUserInfo();

      if (!updateResult.success) {
        alert(`회원 정보 업데이트 실패: ${updateResult.message}`);
        setLoading(false);
        return;
      }

      // userPriceByOne 설정 (예치금 검증을 위해 미리 설정, 소수점 지원)
      let userPriceByOne = parseFloat(formData.userPriceByOne) || 0;
      if (!formData.userPriceByOne && user?.price_grade_detail?.data) {
        const priceDetail = user?.price_grade_detail?.data.find(
          (detail) => detail.mission_template_id === parseInt(selectedSubTab)
        );
        if (priceDetail) {
          userPriceByOne = priceDetail.price;
        }
      }

      // 현재 상태의 예치금 확인
      const userDeposit = user.deposit || 0;

      // 광고 등록 총 금액 계산
      const totalQuantity =
        (parseInt(formData.dailyQuantity) || 0) *
        (parseInt(formData.adDuration) || 0);
      const totalPrice = totalQuantity * userPriceByOne;

      // 예치금 부족 검증
      if (userDeposit < totalPrice) {
        const shortage = totalPrice - userDeposit;
        alert(
          `예치금이 부족합니다.\n\n필요 금액: ${totalPrice.toLocaleString()}원\n보유 예치금: ${userDeposit.toLocaleString()}원\n부족 금액: ${shortage.toLocaleString()}원\n\n충전 후 다시 시도해주세요.`
        );
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error("예치금 검증 중 오류:", error);
      alert("예치금 확인 중 오류가 발생했습니다. 다시 시도해주세요.");
      setLoading(false);
      return;
    }

    // 첫 번째 화면에서 서비스 정보 설정
    const selectedService = missionServices.find(
      (service) => service.id === selectedMainTab
    );
    const selectedAction = missionActions.find(
      (action) => action.id === selectedSubTab
    );

    if (selectedService && selectedAction) {
      setFormData((prev) => ({
        ...prev,
        serviceName: selectedService.originalName, // 메인 탭의 원본 이름 사용
        // actionName은 loadMissionActions나 handleSubTabChange에서 getMissionTemplateAPI로 설정됨
        missionType: selectedService.ms_id,
        missionTemplate: selectedAction.ma_id,
        // userPriceByOne 유지
        userPriceByOne: prev.userPriceByOne,
      }));
    }

    // selectedSubTab이 1일 때 (쇼핑 방문) 크롤링 API 호출
    if (selectedSubTab === 1 && formData.productUrl) {
      try {
        const response = await getNaverSmartStoreAPI(formData.productUrl);

        if (response.data.error) {
          // 에러가 있으면 토스트 알림
          console.error("크롤링 에러:", response.data.error);
          alert(`크롤링 실패: ${response.data.error}`);
          setLoading(false); // 로딩 상태 해제
          return;
        }

        if (response.data.success && response.data.data) {
          const crawledData = response.data.data;

          // 크롤링 데이터로 formData 업데이트
          setFormData((prev) => ({
            ...prev,
            storeName: crawledData.storeName || "",
            missionName: crawledData.productName || "",
            imageUrl: crawledData.imageUrl || "",
            channelUid: crawledData.channelUid || "",
            memo: JSON.stringify({
              originProductName: crawledData.productName,
              originPrice: crawledData.price,
              adTag: crawledData.tags || "",
            }),
            // userPriceByOne 유지
            userPriceByOne: prev.userPriceByOne,
          }));

          // getNaverShoppingReview API 호출
          try {
            if (crawledData.channelUid && formData.productUrl) {
              const reviewResponse = await getNaverShoppingReview(
                crawledData.channelUid,
                formData.productUrl
              );

              // productName이 "리뷰"인 경우 검증
              if (formData.productName === "리뷰") {
                const hasContents =
                  reviewResponse.data?.success &&
                  reviewResponse.data?.data?.secondApiData?.contents &&
                  Array.isArray(
                    reviewResponse.data.data.secondApiData.contents
                  ) &&
                  reviewResponse.data.data.secondApiData.contents.length > 0;

                if (!hasContents) {
                  alert("리뷰를 찾을 수 없어 등록을 하실수 없습니다.");
                  setLoading(false);
                  return;
                }
              }

              // contents가 있으면 formData에 저장
              if (
                reviewResponse.data?.success &&
                reviewResponse.data?.data?.secondApiData?.contents
              ) {
                setFormData((prev) => ({
                  ...prev,
                  contents: reviewResponse.data.data.secondApiData.contents,
                }));
              }
            }
          } catch (reviewError) {
            // productName이 "리뷰"인 경우 에러 시 중단
            if (formData.productName === "리뷰") {
              alert("리뷰를 찾을 수 없어 등록을 하실수 없습니다.");
              setLoading(false);
              return;
            }
            // "리뷰"가 아닌 경우 에러 로그만 남기고 계속 진행
            console.error("리뷰 API 호출 실패:", reviewError);
          }

          // missionName을 분석해서 keywordRanks 생성
          let newKeywordRanks = []; // try 블록 밖에서 선언

          try {
            const missionName = crawledData.productName || "";

            // 1. 상품 제목을 음절별로 끊기
            const syllables = missionName
              .split(/[,\s]+/)
              .filter((word) => word.trim().length > 0);

            if (syllables.length > 0) {
              // 2. 단어 조합 생성
              let keywordCombinations = [];

              if (syllables.length >= 6) {
                // 6개 이상이면 2개, 3개씩 조합
                // 2개씩 조합
                for (let i = 0; i < syllables.length - 1; i++) {
                  for (let j = i + 1; j < syllables.length; j++) {
                    keywordCombinations.push(`${syllables[i]} ${syllables[j]}`);
                  }
                }
                // 3개씩 조합
                for (let i = 0; i < syllables.length - 2; i++) {
                  for (let j = i + 1; j < syllables.length - 1; j++) {
                    for (let k = j + 1; k < syllables.length; k++) {
                      keywordCombinations.push(
                        `${syllables[i]} ${syllables[j]} ${syllables[k]}`
                      );
                    }
                  }
                }
              } else {
                // 6개 미만이면 1개, 2개, 3개씩 조합
                // 1개씩 조합 (단일 단어)
                syllables.forEach((word) => {
                  keywordCombinations.push(word);
                });
                // 2개씩 조합
                for (let i = 0; i < syllables.length - 1; i++) {
                  for (let j = i + 1; j < syllables.length; j++) {
                    keywordCombinations.push(`${syllables[i]} ${syllables[j]}`);
                  }
                }
                // 3개씩 조합
                for (let i = 0; i < syllables.length - 2; i++) {
                  for (let j = i + 1; j < syllables.length - 1; j++) {
                    for (let k = j + 1; k < syllables.length; k++) {
                      keywordCombinations.push(
                        `${syllables[i]} ${syllables[j]} ${syllables[k]}`
                      );
                    }
                  }
                }
              }

              // 3. 최대 30개로 제한하고 keywordRanks 생성
              const limitedCombinations = keywordCombinations.slice(0, 30);

              newKeywordRanks = limitedCombinations.map((keyword) => ({
                keyword: keyword,
                rank: 999,
                selected: false,
              }));

              // 4. keywordRanks 업데이트
              setKeywordRanks(newKeywordRanks);
            }
          } catch (keywordError) {
            console.error("키워드 생성 실패:", keywordError);
            // 키워드 생성 실패해도 계속 진행
          }

          // 네이버 쇼핑 순위 API 호출
          try {
            // sub 키워드 데이터만 먼저 처리
            const subKeywords = newKeywordRanks
              .filter((item) => item.keyword && item.keyword.trim() !== "")
              .map((item) => ({
                productId: formData.productId,
                name: item.keyword,
                pageSize: 1,
              }));

            // 키워드를 10개씩 묶어서 배치 처리
            const batchSize = 10;
            const keywordBatches = [];

            for (let i = 0; i < subKeywords.length; i += batchSize) {
              keywordBatches.push(subKeywords.slice(i, i + batchSize));
            }

            // 각 배치를 1000ms 간격으로 병렬 처리 (429 에러 방지)
            const batchResults = [];

            // 모든 배치를 즉시 시작 (진짜 병렬)
            keywordBatches.forEach((batch, batchIndex) => {
              setTimeout(() => {
                getNaverShoppingRankAPI({
                  sub: batch, // 10개씩 묶인 키워드들을 전송
                })
                  .then((response) => {
                    const result =
                      response.data?.sub ||
                      batch.map((k) => ({ name: k.name, rank: 999 }));

                    // 결과를 해당 인덱스에 저장
                    batchResults[batchIndex] = result;
                  })
                  .catch((error) => {
                    console.error(
                      `배치 ${batchIndex + 1} 순위 조회 실패:`,
                      error.response?.status,
                      error.response?.data?.msg || error.message
                    );

                    // 에러 시 기본값 사용
                    const result = batch.map((k) => ({
                      name: k.name,
                      rank: 999,
                    }));

                    // 결과를 해당 인덱스에 저장
                    batchResults[batchIndex] = result;
                  });
              }, batchIndex * 1000); // 0ms, 1000ms, 2000ms, 3000ms... 순서로 시작
            });

            // 모든 배치가 완료될 때까지 대기
            while (
              batchResults.filter(Boolean).length < keywordBatches.length
            ) {
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
            const subRankResults = batchResults.flat(); // 모든 배치 결과를 하나의 배열로 합치기

            // sub 키워드들의 순위 정보 업데이트
            if (subRankResults && Array.isArray(subRankResults)) {
              // 과거 주문에서 불러온 키워드가 있는지 확인
              const hasPastOrderKeywords = keywordRanks.some(
                (pastItem) => pastItem.selected
              );

              setKeywordRanks((prevKeywordRanks) => {
                // 먼저 모든 키워드의 순위를 업데이트
                const updatedKeywordRanks = prevKeywordRanks.map((item) => {
                  // sub 응답에서 해당 키워드 찾기
                  const subResponse = subRankResults.find(
                    (subItem) => subItem.name === item.keyword
                  );

                  if (subResponse) {
                    const newRank = subResponse.rank || 999;

                    return {
                      ...item,
                      rank: newRank,
                      selected: false, // 일단 모두 false로 설정
                    };
                  }

                  return item;
                });

                // 과거 주문에서 불러온 키워드가 있는지 확인
                const hasPastOrderKeywords = keywordRanks.some(
                  (pastItem) => pastItem.selected
                );

                let finalKeywordRanks;
                let sortedKeywordRanks;

                if (!hasPastOrderKeywords) {
                  // 순위가 낮은 순서로 정렬하여 상위 10개만 선택
                  sortedKeywordRanks = updatedKeywordRanks
                    .filter(
                      (item) => item.rank <= (zoomSettings?.allowRanking || 30)
                    ) // 허용 순위 내의 키워드만 필터링
                    .sort((a, b) => a.rank - b.rank) // 순위가 낮은 순서로 정렬
                    .slice(0, 10); // 상위 6개만 선택

                  // 선택된 키워드들의 selected를 true로 설정하고 순위별로 정렬
                  finalKeywordRanks = updatedKeywordRanks.map((item) => {
                    const isSelected = sortedKeywordRanks.some(
                      (selectedItem) => selectedItem.keyword === item.keyword
                    );

                    return {
                      ...item,
                      selected: isSelected,
                    };
                  });
                } else {
                  // 과거 주문에서 불러온 키워드가 있다면, 과거 주문의 키워드만 selected 유지
                  finalKeywordRanks = updatedKeywordRanks.map((item) => {
                    const isSelected = keywordRanks.some(
                      (pastItem) =>
                        pastItem.keyword === item.keyword && pastItem.selected
                    );

                    return {
                      ...item,
                      selected: isSelected,
                    };
                  });
                }

                // 순위별로 정렬 (낮은 순위가 먼저, 999는 마지막)
                const sortedFinalKeywordRanks = finalKeywordRanks.sort(
                  (a, b) => {
                    // 999는 가장 마지막으로
                    if (a.rank === 999 && b.rank !== 999) return 1;
                    if (a.rank !== 999 && b.rank === 999) return -1;
                    if (a.rank === 999 && b.rank === 999) return 0;

                    // 0은 가장 마지막으로 (999 다음)
                    if (a.rank === 0 && b.rank !== 0) return 1;
                    if (a.rank !== 0 && b.rank === 0) return -1;
                    if (a.rank === 0 && b.rank === 0) return 0;

                    // 일반적인 순위는 낮은 숫자가 먼저
                    return a.rank - b.rank;
                  }
                );

                return sortedFinalKeywordRanks;
              });
            }

            // mainKeyword는 백그라운드에서 별도로 처리
            const updateMainKeyword = async () => {
              try {
                const mainRankData = {
                  main: {
                    productId: formData.productId,
                    name: formData.mainKeyword?.name || "",
                    pageSize: 5,
                  },
                };

                const mainRankResponse = await getNaverShoppingRankAPI(
                  mainRankData
                );

                if (mainRankResponse.data?.main) {
                  const mainData = mainRankResponse.data.main;

                  // mainKeyword의 rank 업데이트
                  setFormData((prev) => ({
                    ...prev,
                    mainKeyword: {
                      ...prev.mainKeyword,
                      rank: mainData.rank || 999,
                    },
                  }));
                }
              } catch (mainKeywordError) {
                console.error("mainKeyword 업데이트 실패:", mainKeywordError);
              }
            };

            // mainKeyword 업데이트를 백그라운드에서 실행
            updateMainKeyword();
          } catch (rankError) {
            console.error("순위 API 호출 실패:", rankError);
            // 순위 API 실패해도 크롤링은 성공했으므로 계속 진행
          }
        }
      } catch (error) {
        console.error("크롤링 API 호출 실패:", error);
        alert("크롤링 중 오류가 발생했습니다.");
        setLoading(false); // 로딩 상태 해제
        return;
      }
      // 쇼핑 원부 일때
    } else if (selectedSubTab === 2 && formData.productUrl) {
      try {
        const response = await getNaverSmartStoreAPI(formData.productUrl);

        if (response.data.error) {
          // 에러가 있으면 토스트 알림
          console.error("크롤링 에러:", response.data.error);
          alert(`크롤링 실패: ${response.data.error}`);
          setLoading(false); // 로딩 상태 해제
          return;
        }

        if (response.data.success && response.data.data) {
          const crawledData = response.data.data;

          // 크롤링 데이터로 formData 업데이트
          setFormData((prev) => ({
            ...prev,
            storeName: crawledData.storeName || "",
            // missionName: crawledData.productName || "",
            price: parseInt(crawledData.price?.replace(/,/g, "")) || 0,
            imageUrl: crawledData.imageUrl || "",
            channelUid: crawledData.channelUid || "",
            memo: JSON.stringify({
              originProductName: crawledData.productName,
              originPrice: crawledData.price,
              adTag: crawledData.tags || "",
            }),
          }));

          // getNaverShoppingReview API 호출
          try {
            if (crawledData.channelUid && formData.productUrl) {
              const reviewResponse = await getNaverShoppingReview(
                crawledData.channelUid,
                formData.productUrl
              );

              // productName이 "리뷰"인 경우 검증
              if (formData.productName === "리뷰") {
                const hasContents =
                  reviewResponse.data?.success &&
                  reviewResponse.data?.data?.secondApiData?.contents &&
                  Array.isArray(
                    reviewResponse.data.data.secondApiData.contents
                  ) &&
                  reviewResponse.data.data.secondApiData.contents.length > 0;

                if (!hasContents) {
                  alert("리뷰를 찾을 수 없어 등록을 하실수 없습니다.");
                  setLoading(false);
                  return;
                }
              }

              // contents가 있으면 formData에 저장
              if (
                reviewResponse.data?.success &&
                reviewResponse.data?.data?.secondApiData?.contents
              ) {
                setFormData((prev) => ({
                  ...prev,
                  contents: reviewResponse.data.data.secondApiData.contents,
                }));
              }
            }
          } catch (reviewError) {
            // productName이 "리뷰"인 경우 에러 시 중단
            if (formData.productName === "리뷰") {
              alert("리뷰를 찾을 수 없어 등록을 하실수 없습니다.");
              setLoading(false);
              return;
            }
            // "리뷰"가 아닌 경우 에러 로그만 남기고 계속 진행
            console.error("리뷰 API 호출 실패:", reviewError);
          }

          // getOriginalInfoMissionAPI 백그라운드 호출
          const performOriginalInfoCheck = async () => {
            try {
              if (formData.missionName && formData.originId) {
                const extractedProductId = extractProductIdFromUrl(
                  formData.originId
                );

                if (extractedProductId) {
                  const originalInfoResponse = await getOriginalInfoMissionAPI(
                    formData.missionName,
                    extractedProductId
                  );

                  if (
                    originalInfoResponse.data?.success &&
                    originalInfoResponse.data?.data
                  ) {
                    const originalData = originalInfoResponse.data.data;

                    // memo에 원부 정보 추가 (기존 키들은 유지)
                    setFormData((prev) => {
                      try {
                        const existingMemo = prev.memo
                          ? JSON.parse(prev.memo)
                          : {};
                        const updatedMemo = JSON.stringify({
                          ...existingMemo,
                          imageUrl: originalData.imageUrl,
                          name: originalData.name,
                          prependUnit: originalData.prependUnit,
                          shoppingProductPrice:
                            originalData.shoppingProductPrice,
                          deliveryFee: originalData.deliveryFee,
                          shoppingProductPriceTotal:
                            originalData.shoppingProductPriceTotal,
                          shoppingProductStar: originalData.shoppingProductStar,
                          shoppingProductStarCount:
                            originalData.shoppingProductStarCount,
                          keep: originalData.keep,
                          shoppingProductMallInformation:
                            originalData.shoppingProductMallInformation,
                        });

                        return {
                          ...prev,
                          memo: updatedMemo,
                        };
                      } catch (error) {
                        console.error("memo JSON 파싱 오류:", error);
                        return prev;
                      }
                    });
                  }
                }
              }
            } catch (error) {
              console.error("getOriginalInfoMissionAPI 호출 실패:", error);
            }
          };

          // 백그라운드에서 원부 정보 체크 실행하고 Promise 생성
          const originalInfoPromise = performOriginalInfoCheck();
          setOriginalInfoPromise(originalInfoPromise);

          // missionName을 분석해서 keywordRanks 생성
          let newKeywordRanks = []; // try 블록 밖에서 선언

          try {
            const missionName = formData.missionName || "";

            // 1. 상품 제목을 음절별로 끊기
            const syllables = missionName
              .split(/[,\s]+/)
              .filter((word) => word.trim().length > 0);

            if (syllables.length > 0) {
              // 2. 단어 조합 생성
              let keywordCombinations = [];

              if (syllables.length >= 6) {
                // 6개 이상이면 2개, 3개씩 조합
                // 2개씩 조합
                for (let i = 0; i < syllables.length - 1; i++) {
                  for (let j = i + 1; j < syllables.length; j++) {
                    keywordCombinations.push(`${syllables[i]} ${syllables[j]}`);
                  }
                }
                // 3개씩 조합
                for (let i = 0; i < syllables.length - 2; i++) {
                  for (let j = i + 1; j < syllables.length - 1; j++) {
                    for (let k = j + 1; k < syllables.length; k++) {
                      keywordCombinations.push(
                        `${syllables[i]} ${syllables[j]} ${syllables[k]}`
                      );
                    }
                  }
                }
              } else {
                // 6개 미만이면 1개, 2개, 3개씩 조합
                // 1개씩 조합 (단일 단어)
                syllables.forEach((word) => {
                  keywordCombinations.push(word);
                });
                // 2개씩 조합
                for (let i = 0; i < syllables.length - 1; i++) {
                  for (let j = i + 1; j < syllables.length; j++) {
                    keywordCombinations.push(`${syllables[i]} ${syllables[j]}`);
                  }
                }
                // 3개씩 조합
                for (let i = 0; i < syllables.length - 2; i++) {
                  for (let j = i + 1; j < syllables.length - 1; j++) {
                    for (let k = j + 1; k < syllables.length; k++) {
                      keywordCombinations.push(
                        `${syllables[i]} ${syllables[j]} ${syllables[k]}`
                      );
                    }
                  }
                }
              }

              // 3. 최대 30개로 제한하고 keywordRanks 생성
              const limitedCombinations = keywordCombinations.slice(0, 30);

              newKeywordRanks = limitedCombinations.map((keyword) => ({
                keyword: keyword,
                rank: 999,
                selected: false,
              }));

              // 4. keywordRanks 업데이트
              setKeywordRanks(newKeywordRanks);
            }
          } catch (keywordError) {
            console.error("키워드 생성 실패:", keywordError);
            // 키워드 생성 실패해도 계속 진행
          }

          // 네이버 쇼핑 순위 API 호출
          try {
            const extractedProductId = extractProductIdFromUrl(
              formData.originId
            );

            if (!extractedProductId) {
              console.error("productId 추출 실패:", formData.originId);
              alert(
                "상품 URL에서 상품 ID를 추출할 수 없습니다. URL을 확인해주세요."
              );
              setLoading(false);
              return;
            }

            // sub 키워드 데이터만 먼저 처리
            const subKeywords = newKeywordRanks
              .filter((item) => item.keyword && item.keyword.trim() !== "")
              .map((item) => ({
                productId: extractedProductId,
                name: item.keyword,
                pageSize: 1,
              }));

            // 키워드를 10개씩 묶어서 배치 처리
            const batchSize = 10;
            const keywordBatches = [];

            for (let i = 0; i < subKeywords.length; i += batchSize) {
              keywordBatches.push(subKeywords.slice(i, i + batchSize));
            }

            // 각 배치를 1000ms 간격으로 병렬 처리 (429 에러 방지)
            const batchResults = [];

            // 모든 배치를 즉시 시작 (진짜 병렬)
            keywordBatches.forEach((batch, batchIndex) => {
              setTimeout(() => {
                getNaverShoppingRankAPI({
                  sub: batch, // 10개씩 묶인 키워드들을 전송
                })
                  .then((response) => {
                    const result =
                      response.data?.sub ||
                      batch.map((k) => ({ name: k.name, rank: 999 }));

                    // 결과를 해당 인덱스에 저장
                    batchResults[batchIndex] = result;
                  })
                  .catch((error) => {
                    console.error(
                      `배치 ${batchIndex + 1} 순위 조회 실패:`,
                      error.response?.status,
                      error.response?.data?.msg || error.message
                    );

                    // 429 에러 시 1초 후 재시도
                    if (error.response?.status === 429) {
                      setTimeout(() => {
                        getNaverShoppingRankAPI({
                          sub: batch, // 10개씩 묶인 키워드들을 전송
                        })
                          .then((response) => {
                            const result =
                              response.data?.sub ||
                              batch.map((k) => ({ name: k.name, rank: 999 }));

                            // 결과를 해당 인덱스에 저장
                            batchResults[batchIndex] = result;
                          })
                          .catch((retryError) => {
                            console.error(
                              `배치 ${batchIndex + 1} 재시도 실패:`,
                              retryError.response?.status,
                              retryError.response?.data?.msg ||
                                retryError.message
                            );

                            // 재시도 실패 시 기본값 사용
                            const result = batch.map((k) => ({
                              name: k.name,
                              rank: 999,
                            }));

                            // 결과를 해당 인덱스에 저장
                            batchResults[batchIndex] = result;
                          });
                      }, 1000); // 1초 후 재시도
                    } else {
                      // 429가 아닌 다른 에러는 기본값 사용
                      const result = batch.map((k) => ({
                        name: k.name,
                        rank: 999,
                      }));

                      // 결과를 해당 인덱스에 저장
                      batchResults[batchIndex] = result;
                    }
                  });
              }, batchIndex * 1000); // 0ms, 1000ms, 2000ms, 3000ms... 순서로 시작
            });

            // 모든 배치가 완료될 때까지 대기
            while (
              batchResults.filter(Boolean).length < keywordBatches.length
            ) {
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
            const subRankResults = batchResults.flat(); // 모든 배치 결과를 하나의 배열로 합치기

            // sub 키워드들의 순위 정보 업데이트
            if (subRankResults && Array.isArray(subRankResults)) {
              setKeywordRanks((prevKeywordRanks) => {
                // 먼저 모든 키워드의 순위를 업데이트
                const updatedKeywordRanks = prevKeywordRanks.map((item) => {
                  // sub 응답에서 해당 키워드 찾기
                  const subResponse = subRankResults.find(
                    (subItem) => subItem.name === item.keyword
                  );

                  if (subResponse) {
                    const newRank = subResponse.rank || 999;

                    return {
                      ...item,
                      rank: newRank,
                      selected: false, // 일단 모두 false로 설정
                    };
                  }

                  return item;
                });

                // 과거 주문에서 불러온 키워드가 있는지 확인
                const hasPastOrderKeywords = keywordRanks.some(
                  (pastItem) => pastItem.selected
                );

                let finalKeywordRanks;
                let sortedKeywordRanks;

                if (!hasPastOrderKeywords) {
                  // 순위가 낮은 순서로 정렬하여 상위 10개만 선택
                  sortedKeywordRanks = updatedKeywordRanks
                    .filter(
                      (item) => item.rank <= (zoomSettings?.allowRanking || 30)
                    ) // 허용 순위 내의 키워드만 필터링
                    .sort((a, b) => a.rank - b.rank) // 순위가 낮은 순서로 정렬
                    .slice(0, 10); // 상위 6개만 선택

                  // 선택된 키워드들의 selected를 true로 설정하고 순위별로 정렬
                  finalKeywordRanks = updatedKeywordRanks.map((item) => {
                    const isSelected = sortedKeywordRanks.some(
                      (selectedItem) => selectedItem.keyword === item.keyword
                    );

                    return {
                      ...item,
                      selected: isSelected,
                    };
                  });
                } else {
                  // 과거 주문에서 불러온 키워드가 있다면, 과거 주문의 키워드만 selected 유지
                  finalKeywordRanks = updatedKeywordRanks.map((item) => {
                    const isSelected = keywordRanks.some(
                      (pastItem) =>
                        pastItem.keyword === item.keyword && pastItem.selected
                    );

                    return {
                      ...item,
                      selected: isSelected,
                    };
                  });
                }

                // 순위별로 정렬 (낮은 순위가 먼저, 999는 마지막)
                const sortedFinalKeywordRanks = finalKeywordRanks.sort(
                  (a, b) => {
                    // 999는 가장 마지막으로
                    if (a.rank === 999 && b.rank !== 999) return 1;
                    if (a.rank !== 999 && b.rank === 999) return -1;
                    if (a.rank === 999 && b.rank === 999) return 0;

                    // 0은 가장 마지막으로 (999 다음)
                    if (a.rank === 0 && b.rank !== 0) return 1;
                    if (a.rank !== 0 && b.rank === 0) return -1;
                    if (a.rank === 0 && b.rank === 0) return 0;

                    // 일반적인 순위는 낮은 숫자가 먼저
                    return a.rank - b.rank;
                  }
                );

                return sortedFinalKeywordRanks;
              });
            }

            // mainKeyword는 백그라운드에서 별도로 처리
            const updateMainKeyword = async () => {
              try {
                const mainRankData = {
                  main: {
                    productId: extractedProductId,
                    name: formData.mainKeyword?.name || "",
                    pageSize: 5,
                  },
                };

                const mainRankResponse = await getNaverShoppingRankAPI(
                  mainRankData
                );

                if (mainRankResponse.data?.main) {
                  const mainData = mainRankResponse.data.main;

                  // mainKeyword의 rank 업데이트
                  setFormData((prev) => ({
                    ...prev,
                    mainKeyword: {
                      ...prev.mainKeyword,
                      rank: mainData.rank || 999,
                    },
                  }));
                }
              } catch (mainKeywordError) {
                console.error("mainKeyword 업데이트 실패:", mainKeywordError);
              }
            };

            // mainKeyword 업데이트를 백그라운드에서 실행
            updateMainKeyword();
          } catch (rankError) {
            console.error("순위 API 호출 실패:", rankError);
            // 순위 API 실패해도 크롤링은 성공했으므로 계속 진행
          }
        }
      } catch (error) {
        console.error("크롤링 API 호출 실패:", error);
        alert("크롤링 중 오류가 발생했습니다.");
        setLoading(false); // 로딩 상태 해제
        return;
      }
    } else if (
      (selectedSubTab === 3 || selectedSubTab === 4) &&
      formData.productUrl
    ) {
      try {
        const response = await getNaverPlayStoreAPI(formData.productUrl);

        if (response.data.error) {
          // 에러가 있으면 토스트 알림
          console.error("크롤링 에러:", response.data.error);
          alert(`크롤링 실패: ${response.data.error}`);
          setLoading(false); // 로딩 상태 해제
          return;
        }

        if (response.data.success && response.data.data) {
          const crawledData = response.data.data;

          // 크롤링 데이터로 formData 업데이트
          setFormData((prev) => ({
            ...prev,
            storeName: decodeHtmlEntities(crawledData.storeName) || "",
            // missionName: crawledData.productName || "",
            imageUrl: crawledData.imageUrl || "",
            missionName: decodeHtmlEntities(crawledData.storeName) || "",
          }));

          // 네이버 쇼핑 순위 API 호출 (백그라운드로 처리)
          const performRankCheck = async () => {
            try {
              const extractedProductId = extractProductIdFromUrl(
                formData.productId
              );

              if (!extractedProductId) {
                console.error("productId 추출 실패:", formData.productId);
                alert(
                  "상품 URL에서 상품 ID를 추출할 수 없습니다. selectedSubTab === 3 || selectedSubTab === 4 URL을 확인해주세요."
                );
                return 999; // 기본값 반환
              }

              const rankData = {
                main: {
                  productId: extractedProductId,
                  name: formData.mainKeyword?.name || "",
                  pageSize: 4,
                },
              };

              // v플레이스 메인 키워드 확인
              const rankResponse = await getNaverPlayStoreRankAPI(rankData);

              if (rankResponse.data?.main) {
                const mainData = rankResponse.data.main;

                const updatedRank = mainData.rank || 999;

                // mainKeyword의 rank 업데이트
                setFormData((prev) => ({
                  ...prev,
                  mainKeyword: {
                    ...prev.mainKeyword,
                    rank: updatedRank,
                  },
                }));

                return updatedRank; // 업데이트된 rank 값 반환
              }

              return 999; // 기본값 반환
            } catch (rankError) {
              console.error("순위 API 호출 실패:", rankError);
              // 순위 API 실패해도 크롤링은 성공했으므로 계속 진행
              return 999; // 기본값 반환
            }
          };

          // 백그라운드에서 순위 체크 실행하고 Promise 생성
          const rankPromise = performRankCheck();
          setPlayStoreRankPromise(rankPromise);

          // productName이 "길찾기"인 경우 getRouteInfoAPI 백그라운드 호출
          if (
            formData.productName === "길찾기" ||
            formData.productName === "복합"
          ) {
            // 병렬 실행을 위해 즉시 시작
            const routeInfoPromise = (async () => {
              try {
                const routeInfoResponse = await getRouteInfoAPI(
                  formData.productUrl
                );

                if (
                  routeInfoResponse.data?.success &&
                  routeInfoResponse.data?.data
                ) {
                  const routeData = routeInfoResponse.data.data;

                  // memo에 길찾기 정보 추가 (기존 키들은 유지)
                  setFormData((prev) => {
                    try {
                      const existingMemo = prev.memo
                        ? JSON.parse(prev.memo)
                        : {};
                      const updatedMemo = JSON.stringify({
                        ...existingMemo,
                        quizzes: routeData.quizzes,
                        tripSummaryList:
                          routeData.originalData?.tripSummaryList || [],
                      });

                      return {
                        ...prev,
                        memo: updatedMemo,
                      };
                    } catch (error) {
                      console.error("memo JSON 파싱 오류:", error);
                      return prev;
                    }
                  });
                } else {
                  console.warn(
                    "⚠️ getRouteInfoAPI 응답이 성공하지 않음:",
                    routeInfoResponse.data
                  );
                }
              } catch (error) {
                console.error("❌ getRouteInfoAPI 호출 실패:", error);
              }
            })();
          }
        }

        if (selectedSubTab === 4) {
          try {
            // getMemberAdAPI 호출하여 데이터 가져오기
            const adResponse = await getMemberAdAPI(
              user.id, // webMemberId
              1, // page
              2000, // limit
              {
                missionType: selectedMainTab, // selectedMainTab이 missionType
                missionTemplate: selectedSubTab, // selectedSubTab이 missionTemplate
              }
            );

            if (adResponse.data?.data) {
              const ads = adResponse.data.data;

              // productId별로 ad_id들을 그룹화
              const productIdGroups = {};

              ads.forEach((ad) => {
                const productId = ad.productId;
                if (!productIdGroups[productId]) {
                  productIdGroups[productId] = [];
                }
                productIdGroups[productId].push(ad.ad_id);
              });

              // 현재 입력된 productId와 같은 광고들의 ad_id 찾기
              const currentProductId = formData.productId;
              if (currentProductId && productIdGroups[currentProductId]) {
                const sameProductAdIds = productIdGroups[currentProductId];

                // memo에 duplicateIds 추가
                setFormData((prev) => {
                  try {
                    // 기존 memo 파싱
                    const existingMemo = prev.memo ? JSON.parse(prev.memo) : {};

                    // duplicateIds 추가 (기존 데이터는 유지)
                    const updatedMemo = JSON.stringify({
                      ...existingMemo,
                      duplicateIds: sameProductAdIds,
                    });

                    return {
                      ...prev,
                      memo: updatedMemo,
                    };
                  } catch (error) {
                    console.error("memo JSON 파싱 오류:", error);
                    // 파싱 실패 시 새로운 memo 생성
                    const updatedMemo = JSON.stringify({
                      duplicateIds: sameProductAdIds,
                    });

                    return {
                      ...prev,
                      memo: updatedMemo,
                    };
                  }
                });
              } else {
                // 같은 productId가 없는 경우 duplicateIds를 빈 배열로 설정
                setFormData((prev) => {
                  try {
                    const existingMemo = prev.memo ? JSON.parse(prev.memo) : {};
                    const updatedMemo = JSON.stringify({
                      ...existingMemo,
                      duplicateIds: [],
                    });

                    return {
                      ...prev,
                      memo: updatedMemo,
                    };
                  } catch (error) {
                    console.error("memo JSON 파싱 오류:", error);
                    const updatedMemo = JSON.stringify({
                      duplicateIds: [],
                    });

                    return {
                      ...prev,
                      memo: updatedMemo,
                    };
                  }
                });
              }
            }
          } catch (error) {
            console.error("getMemberAdAPI 호출 실패:", error);
          }
        }
      } catch (error) {
        console.error("크롤링 API 호출 실패:", error);
        alert("크롤링 중 오류가 발생했습니다.");
        setLoading(false); // 로딩 상태 해제
        return;
      }
    }
    // if (
    // selectedSubTab === 5 &&
    // selectedSubTab === 6 &&
    // (formData.googlePlayUrl || formData.appStoreUrl)
    // )
    else {
      // 플레이스 방문 크롤링
      try {
        const response = await getAppStoreAPI(
          formData.googlePlayUrl || null,
          formData.appStoreUrl || null
        );

        if (response.data.error) {
          // 에러가 있으면 토스트 알림
          console.error("크롤링 에러:", response.data.error);
          alert(`크롤링 실패: ${response.data.error}`);
          setLoading(false); // 로딩 상태 해제
          return;
        }

        if (response.data.success && response.data.data) {
          const crawledData = response.data.data;

          console.log("crawledData", crawledData);

          // 크롤링 데이터로 formData 업데이트
          setFormData((prev) => ({
            ...prev,
            storeName: crawledData.storeName || "",
            // missionName: crawledData.productName || "",
            productName: crawledData.appName || "",
            imageUrl: crawledData.imageUrl || "",
            missionName: crawledData.appName || "",
          }));

          // selectedSubTab이 5나 6일 때 getMemberAdAPI 호출하여 데이터 가져오기
          // if (selectedSubTab === 5 || selectedSubTab === 6) {
          try {
            // getMemberAdAPI 호출하여 데이터 가져오기
            const adResponse = await getAllMemberAdAPI(
              // user.id, // webMemberId
              1, // page
              2000, // limit
              {
                serviceName: "리뷰", // missionType은 '리뷰'
                // missionTemplate: selectedSubTab, // selectedSubTab이 missionTemplate
                missionName: crawledData.appName,
              }
            );

            console.log("adResponse", adResponse);

            if (adResponse.data?.data) {
              const ads = adResponse.data.data;

              // 모든 ad_id를 배열로 수집
              const duplicateAdIds = [];

              ads.forEach((ad) => {
                duplicateAdIds.push(ad.ad_id);
              });

              console.log("중복 광고 ad_id 목록:", duplicateAdIds);

              if (duplicateAdIds.length > 0) {
                // memo에 duplicateIds 추가
                setFormData((prev) => {
                  try {
                    // 기존 memo 파싱
                    const existingMemo = prev.memo ? JSON.parse(prev.memo) : {};

                    // duplicateIds 추가 (기존 데이터는 유지)
                    const updatedMemo = JSON.stringify({
                      ...existingMemo,
                      duplicateIds: duplicateAdIds,
                    });

                    console.log("✅ memo 업데이트 완료:", updatedMemo);

                    return {
                      ...prev,
                      memo: updatedMemo,
                    };
                  } catch (error) {
                    console.error("memo JSON 파싱 오류:", error);
                    // 파싱 실패 시 새로운 memo 생성
                    const updatedMemo = JSON.stringify({
                      duplicateIds: duplicateAdIds,
                    });

                    console.log("✅ memo 새로 생성:", updatedMemo);

                    return {
                      ...prev,
                      memo: updatedMemo,
                    };
                  }
                });
              } else {
                console.log("⚠️ duplicateAdIds가 비어있습니다");
              }
            }
          } catch (error) {
            console.error("getAllMemberAdAPI 호출 실패:", error);
          }
        }
      } catch (error) {
        console.error("크롤링 API 호출 실패:", error);
        alert("크롤링 중 오류가 발생했습니다.");
        setLoading(false); // 로딩 상태 해제
        return;
      }
    }

    // userPriceByOne이 설정되지 않았다면 현재 selectedSubTab에 대해 설정 (소수점 지원)
    let userPriceByOne = parseFloat(formData.userPriceByOne) || 0;
    if (!formData.userPriceByOne && user?.price_grade_detail?.data) {
      const priceDetail = user?.price_grade_detail?.data.find(
        (detail) => detail.mission_template_id === parseInt(selectedSubTab)
      );
      if (priceDetail) {
        userPriceByOne = priceDetail.price;
      }
    }

    const totalQuantity = (() => {
      // 앱 리뷰(selectedSubTab 5, 6)의 경우 totalQuantity를 직접 사용
      if (selectedSubTab === 5 || selectedSubTab === 6) {
        return parseInt(formData.totalQuantity) || 0;
      }

      // 다른 광고 타입의 경우 adEndDate가 있으면 adDuration을 사용하여 계산
      if (formData.adEndDate && formData.adStartDate) {
        const adDuration = parseInt(formData.adDuration) || 0;
        if (adDuration > 0) {
          return (parseInt(formData.dailyQuantity) || 0) * adDuration;
        }
      }
      // 기존 로직: totalQuantity 직접 입력
      return parseInt(formData.totalQuantity) || 0;
    })();
    const price = userPriceByOne * totalQuantity;

    let endDate = "";
    if (formData.adEndDate) {
      // adEndDate가 있으면 그 값을 사용
      endDate = formData.adEndDate;
    } else if (formData.adStartDate && formData.adDuration) {
      // 기존 로직: adStartDate + adDuration으로 계산
      const startDate = new Date(formData.adStartDate);
      const endDateObj = new Date(startDate);
      endDateObj.setDate(
        startDate.getDate() + (parseInt(formData.adDuration) || 0) - 1
      );
      endDate = endDateObj.toISOString().slice(0, 10);
    }

    // 계산된 값들로 formData 업데이트 (userPriceByOne도 포함)
    setFormData((prev) => {
      // memo의 unitPrice도 함께 업데이트
      let updatedMemo = prev.memo;
      try {
        const existingMemo = prev.memo ? JSON.parse(prev.memo) : {};
        updatedMemo = JSON.stringify({
          ...existingMemo,
          unitPrice: String(userPriceByOne || 0),
        });
      } catch (error) {
        console.error("memo JSON 파싱 오류:", error);
        updatedMemo = JSON.stringify({
          unitPrice: String(userPriceByOne || 0),
        });
      }

      return {
        ...prev,
        userPriceByOne: userPriceByOne,
        totalQuantity: totalQuantity,
        price: price,
        endDate: endDate,
        memo: updatedMemo,
      };
    });
    
    // selectedSubTab이 13인 경우 바로 주문하기 (2단계 건너뛰기)
    if (selectedSubTab === 13) {
      setLoading(false); // 로딩 상태 해제
      // handleSubmit을 다음 이벤트 루프에서 호출 (함수 호이스팅 문제 방지)
      setTimeout(() => {
        handleSubmit();
      }, 0);
      return;
    }
    // 즉시 두 번째 화면으로 이동 (formData 업데이트는 비동기적으로 처리됨)
    setCurrentStep(2);
    setLoading(false); // 로딩 상태 해제

    // getNaverPlayStoreRankAPI가 있는 경우 0.5초 더 기다림 (딜레이 최소화)
    if (selectedSubTab === 3 || selectedSubTab === 4) {
      setTimeout(() => {
        // 0.5초 후 추가 작업이 필요한 경우 여기에 작성
      }, 500);
    }

    // getOriginalInfoMissionAPI가 있는 경우 0.5초 더 기다림 (딜레이 최소화)
    if (selectedSubTab === 2) {
      setTimeout(() => {
        // 0.5초 후 추가 작업이 필요한 경우 여기에 작성
      }, 500);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  // 복합 구동 방식일 때 키워드별 수량 합계 검증
  const validateCompositeQuantity = () => {
    if (formData.productName !== "복합") {
      return { isValid: true, message: "" };
    }

    const keywordQuantitySum = keywordRanks
      .filter((item) => item.selected)
      .reduce((sum, item) => {
        const quantity = parseInt(item.quantity) || 0;
        return sum + quantity;
      }, 0);

    const dailyQuantity = parseInt(formData.dailyQuantity) || 0;

    if (keywordQuantitySum !== dailyQuantity) {
      return {
        isValid: false,
        message: `키워드별 수량 합계(${keywordQuantitySum}회)가 일일 수량(${dailyQuantity}회)과 일치하지 않습니다.`,
      };
    }

    // 모든 키워드에 수량이 입력되었는지 확인
    const selectedKeywords = keywordRanks.filter((item) => item.selected);
    const keywordsWithoutQuantity = selectedKeywords.filter(
      (item) => !item.quantity || parseInt(item.quantity) === 0
    );

    if (keywordsWithoutQuantity.length > 0) {
      return {
        isValid: false,
        message: "모든 키워드에 수량을 입력해주세요.",
      };
    }

    return { isValid: true, message: "" };
  };
 // 쿠팡 방문(selectedSubTab === 13)용 검증 함수
  const validateCoupangVisit = () => {
     const userIsSpecial = isSpecialUser(user?.id);

      if (userIsSpecial) {
        return true;
      }
    // 일일 수량 검증: 최소 400, 400의 배수
    const dailyQuantity = parseInt(formData.dailyQuantity) || 0;
    if (dailyQuantity < 400) {
      alert("일일 수량은 최소 400회 이상이어야 합니다.");
      return false;
    }
    if (dailyQuantity % 400 !== 0) {
      alert("일일 수량은 400의 배수여야 합니다.");
      return false;
    }

    // 진행 일수 검증: 최소 30, 30의 배수
    const adDuration = parseInt(formData.adDuration) || 0;
    if (adDuration < 30) {
      alert("진행 일수는 최소 30일 이상이어야 합니다.");
      return false;
    }
    if (adDuration % 30 !== 0) {
      alert("진행 일수는 30의 배수여야 합니다.");
      return false;
    }

    // 광고 시작일과 종료일이 있을 때 진행 일수 검증
    if (formData.adStartDate && formData.adEndDate) {
      const startDate = new Date(formData.adStartDate);
      const endDate = new Date(formData.adEndDate);
      if (endDate >= startDate) {
        const timeDiff = endDate.getTime() - startDate.getTime();
        const calculatedDays = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
        
        // 계산된 일수와 입력된 진행 일수가 일치하는지 확인
        if (calculatedDays !== adDuration) {
          alert(
            `입력하신 진행 일수(${adDuration}일)와 광고 기간(${calculatedDays}일)이 일치하지 않습니다.`
          );
          return false;
        }

        // 계산된 일수도 30의 배수인지 확인
        if (calculatedDays < 30) {
          alert("광고 기간은 최소 30일 이상이어야 합니다.");
          return false;
        }
        if (calculatedDays % 30 !== 0) {
          alert("광고 기간은 30의 배수여야 합니다.");
          return false;
        }
      }
    }

    return true;
  };

  // 주문하기 버튼 클릭 핸들러
  const handleSubmit = async () => {
    // 로그인하지 않은 경우 로그인 화면으로 이동
    if (!user?.id) {
      navigate("/login?redirect=home&tab=addAdvertisement");
      return;
    }

    // 쿠팡 방문(selectedSubTab === 13)일 때 검증 후 경고 모달 표시
    if (selectedSubTab === 13) {
      // 먼저 검증 수행
      if (!validateCoupangVisit()) {
        return; // 검증 실패 시 중단
      }
      // 검증 통과 시 경고 모달 표시
      setShowConfirmModal(true);
      return;
    }

    // 그 외의 경우 바로 주문 처리
    await processSubmit();
  };

  // 확인 모달에서 확인 버튼 클릭 시
  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);
    await processSubmit();
  };

  // 확인 모달에서 취소 버튼 클릭 시
  const handleCancelSubmit = () => {
    setShowConfirmModal(false);
  };

  // 실제 주문 처리 함수
  const processSubmit = async () => {
    // 쿠팡 방문(selectedSubTab === 13)일 때 일일 수량 및 진행 일수 검증
    if (selectedSubTab === 13) {
      // 일일 수량 검증: 최소 400, 400의 배수
      const dailyQuantity = parseInt(formData.dailyQuantity) || 0;
      if (dailyQuantity < 400) {
        alert("일일 수량은 최소 400회 이상이어야 합니다.");
        return;
      }
      if (dailyQuantity % 400 !== 0) {
        alert("일일 수량은 400의 배수여야 합니다.");
        return;
      }

      // 진행 일수 검증: 최소 30, 30의 배수
      const adDuration = parseInt(formData.adDuration) || 0;
      if (adDuration < 30) {
        alert("진행 일수는 최소 30일 이상이어야 합니다.");
        return;
      }
      if (adDuration % 30 !== 0) {
        alert("진행 일수는 30의 배수여야 합니다.");
        return;
      }

      // 광고 시작일과 종료일이 있을 때 진행 일수 검증
      if (formData.adStartDate && formData.adEndDate) {
        const startDate = new Date(formData.adStartDate);
        const endDate = new Date(formData.adEndDate);
        if (endDate >= startDate) {
          const timeDiff = endDate.getTime() - startDate.getTime();
          const calculatedDays = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
          
          // 계산된 일수와 입력된 진행 일수가 일치하는지 확인
          if (calculatedDays !== adDuration) {
            alert(
              `입력하신 진행 일수(${adDuration}일)와 광고 기간(${calculatedDays}일)이 일치하지 않습니다.`
            );
            return;
          }

          // 계산된 일수도 30의 배수인지 확인
          if (calculatedDays < 30) {
            alert("광고 기간은 최소 30일 이상이어야 합니다.");
            return;
          }
          if (calculatedDays % 30 !== 0) {
            alert("광고 기간은 30의 배수여야 합니다.");
            return;
          }
        }
      }
    }

    // 쿠팡 방문(13)이 아닌 다른 탭(1, 2, 3, 4)일 때 일일 수량 검증: 최소 100, 100의 배수
    if (
      selectedSubTab === 1 ||
      selectedSubTab === 2 ||
      selectedSubTab === 3 ||
      selectedSubTab === 4
    ) {
      const userIsSpecial = isSpecialUser(user?.id);
      if (!userIsSpecial) {
        const dailyQuantity = parseInt(formData.dailyQuantity) || 0;
        if (dailyQuantity < 100) {
          alert("일일 수량은 최소 100회 이상이어야 합니다.");
          return;
        }
        if (dailyQuantity % 100 !== 0) {
          alert("일일 수량은 100의 배수여야 합니다.");
          return;
        }
      }
    }

    // 복합 구동 방식일 때 수량 검증
    const quantityValidation = validateCompositeQuantity();
    if (!quantityValidation.isValid) {
      alert(quantityValidation.message);
      return;
    }

    // 로딩 상태 시작
    setLoading(true);

    // selectedSubTab이 3 또는 4인 경우 백그라운드 API 완료 대기
    let updatedMainKeywordRank = formData.mainKeyword?.rank || 999;

    if (
      (selectedSubTab === 3 || selectedSubTab === 4) &&
      playStoreRankPromise
    ) {
      try {
        updatedMainKeywordRank = await playStoreRankPromise;
      } catch (error) {
        console.error("getNaverPlayStoreRankAPI 대기 중 오류:", error);
      }
    }

    // selectedSubTab이 2인 경우 원부 정보 API 완료 대기
    if (selectedSubTab === 2 && originalInfoPromise) {
      try {
        await originalInfoPromise;
      } catch (error) {
        console.error("getOriginalInfoMissionAPI 대기 중 오류:", error);
      }
    }

    // selectedSubTab이 13(쿠팡 방문): 백그라운드 Promise가 있으면 await, 없으면 이 시점에 API 호출
    let coupangProductInfo = null;
    if (selectedSubTab === 13) {
      const mainKeywordName = formData.mainKeyword?.name || "";
      const productUrl = formData.productUrl || "";
      try {
        // if (coupangRankPromise) {
        //   updatedMainKeywordRank = await coupangRankPromise;
        // } else {
        //   const rankRes = await getCoupangProductRankAPI(mainKeywordName, productUrl);
        //   updatedMainKeywordRank = rankRes?.data?.organicRank ?? 999;
        // }
        if (coupangProductInfoPromise) {
          coupangProductInfo = await coupangProductInfoPromise;
        } else {
          const infoRes = await getCoupangProductInfoAPI(productUrl);
          if (infoRes?.data?.success && infoRes?.data?.data) {
            coupangProductInfo = infoRes.data.data;
          }
        }
        if (!coupangProductInfo) {
          alert("쿠팡 상품 정보를 가져오지 못했습니다.");
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error("쿠팡 API 오류:", err);
        alert(err?.response?.data?.msg || "쿠팡 상품 정보 조회 중 오류가 발생했습니다.");
        setLoading(false);
        return;
      }
    }

    // selectedSubTab이 1이나 2, 13일 때 키워드 검증
    if (selectedSubTab === 1 || selectedSubTab === 2 || selectedSubTab === 13) {
      // selectedSubTab이 13일 때는 메인 키워드를 서브 키워드에 자동 추가
      let updatedKeywordRanks = [...keywordRanks];
      if (selectedSubTab === 13) {
        const mainKeywordName = formData.mainKeyword?.name || "";
        if (mainKeywordName.trim() !== "") {
          // 메인 키워드가 이미 서브 키워드에 있는지 확인
          const mainKeywordIndex = updatedKeywordRanks.findIndex(
            (item) => item.keyword.trim() === mainKeywordName.trim()
          );
          
          if (mainKeywordIndex === -1) {
            // 메인 키워드가 서브 키워드에 없으면 추가
            updatedKeywordRanks.push({
              keyword: mainKeywordName.trim(),
              rank: formData.mainKeyword?.rank || 999,
              selected: true,
            });
          } else {
            // 이미 있으면 selected를 true로 설정
            updatedKeywordRanks[mainKeywordIndex] = {
              ...updatedKeywordRanks[mainKeywordIndex],
              selected: true,
            };
          }
          // keywordRanks 상태 업데이트
          setKeywordRanks(updatedKeywordRanks);
        }
      }

      const selectedKeywords = updatedKeywordRanks.filter(
        (item) => item.selected && item.keyword && item.keyword.trim() !== ""
      );

      // selected: true이고 keyword가 비어있지 않은 것이 1개 이상인지 확인
      if (selectedKeywords.length === 0) {
        alert("최소 1개 이상의 키워드를 선택하고 입력해주세요.");
        setLoading(false);
        return;
      }
      // selectedSubTab이 13이 아닐 때만 키워드 순위 확인
      if (selectedSubTab !== 13) {
        // 모든 selected: true의 rank가 허용 순위보다 낮은지 확인
        const allowRanking = zoomSettings?.allowRanking || 30;
        const invalidKeywords = selectedKeywords.filter(
          (item) => item.rank > allowRanking
        );

        if (invalidKeywords.length > 0) {
          const invalidKeywordNames = invalidKeywords
            .map((item) => item.keyword)
            .join(", ");
          alert(
            `${allowRanking}위 초과 키워드가 있습니다: ${invalidKeywordNames}\n모든 키워드가 ${allowRanking}위 이상이여야 합니다.`
          );
          setLoading(false);
          return;
        }      }
    } else if (selectedSubTab === 3 || selectedSubTab === 4) {
      // keyword가 비어있지 않은 것이 1개 이상인지 확인
      const validKeywords = keywordRanks.filter(
        (item) => item.keyword && item.keyword.trim() !== ""
      );
      if (validKeywords.length === 0) {
        alert("최소 1개 이상의 키워드를 입력해주세요.");
        setLoading(false);
        return;
      }
    } else if (selectedSubTab === 5) {
      if (!formData.googlePlayUrl) {
        alert("앱 스토어 URL을 입력해주세요.");
        setLoading(false);
        return;
      }
    } else if (selectedSubTab === 6) {
      if (!formData.appStoreUrl) {
        alert("앱 스토어 URL을 입력해주세요.");
        setLoading(false);
        return;
      }
    }

    // 공통 필드들
    const commonFields = {
      // 서비스 정보 (첫 번째 화면에서 설정됨)
      serviceName: formData.serviceName,
      actionName: formData.actionName,
      missionName: formData.missionName || "",
      missionType: formData.missionType,
      missionTemplate: formData.missionTemplate,
      rewardSilver: formData.rewardSilver,
      // 웹 회원 ID
      webMemberId: user.id,
      productName: formData.productName,
      storeName: formData.storeName,

      openType: formData.openType,
      openWebSite: formData.openWebSite,
      rewardSilver: formData.rewardSilver,

      // productUrl

      productId: formData.productId,
      originId: formData.originId, // 원산지 상품 URL

      imageUrl: formData.imageUrl,

      // 상태 및 메모
      status: "PENDING",
      memo: (() => {
        try {
          // 기존 memo가 있으면 파싱, 없으면 빈 객체
          const existingMemo = formData.memo ? JSON.parse(formData.memo) : {};

          // 새로운 memo 객체 생성 (기존 데이터 유지하면서 업데이트)
          const newMemo = {
            ...existingMemo,
            unitPrice: String(formData.userPriceByOne || 0),
            originProductName: existingMemo.originProductName || "",
            originPrice: existingMemo.originPrice || "",
            adTag: existingMemo.adTag || "",
          };

          return JSON.stringify(newMemo);
        } catch (error) {
          console.error("memo JSON 파싱 오류:", error);
          // 파싱 실패 시 새로운 memo 생성
          return JSON.stringify({
            unitPrice: String(formData.userPriceByOne || 0),
            originProductName: "",
            originPrice: "",
            adTag: "",
          });
        }
      })(),

      // // 가격 및 수량
      price: formData.price,
      totalQuantity:
        (parseInt(formData.dailyQuantity) || 0) *
        (parseInt(formData.adDuration) || 0),
      dailyQuantity: parseInt(formData.dailyQuantity) || 0,

      // 날짜 정보
      startDate: formData.adStartDate
        ? // ? `${formData.adStartDate}T00:00:00.000Z`
          `${formData.adStartDate}T00:00:00`
        : "",
      // endDate: formData.adEndDate ? `${formData.adEndDate}T23:59:59.999Z` : "",
      endDate: formData.adEndDate ? `${formData.adEndDate}T23:59:59` : "",

      // 승인 답변
      approveAnswer: formData.approveAnswer,

      // 체류 시간
      mustRemainingTime: formData.mustRemainingTime,

      // detail // /0 -> 앱 리뷰  1 => 쇼핑(api), 2 => 플레이스(크롤링)

      // // 미션 중에 하루 미션 완료 한도 있는 키
      // needUpdata: formData.needUpdata,

      // 키워드 정보 (업데이트된 rank 값 사용)
     mainKeyword: {
        name: formData.mainKeyword.name,
        rank: updatedMainKeywordRank,
      },
      subKeywords: (() => {
        // selectedSubTab이 13일 때는 메인 키워드도 서브 키워드에 포함
        const subKeywordsFromRanks = keywordRanks
          .filter((item) => item.selected)
          .map((item) => {
            const keywordData = {
              name: item.keyword,
            };

            // 복합 구동 방식일 때 actionType과 quantity 추가
            if (formData.productName === "복합") {
              keywordData.actionType = item.actionType || "길찾기";
              keywordData.quantity = parseInt(item.quantity) || 0;
            }

            return keywordData;
          });

        // selectedSubTab이 13일 때 메인 키워드가 서브 키워드에 없으면 추가
        if (selectedSubTab === 13) {
          const mainKeywordName = formData.mainKeyword?.name || "";
          if (mainKeywordName.trim() !== "") {
            const mainKeywordExists = subKeywordsFromRanks.some(
              (item) => item.name.trim() === mainKeywordName.trim()
            );
            if (!mainKeywordExists) {
              subKeywordsFromRanks.push({
                name: mainKeywordName.trim(),
              });
            }
          }
        }

        return subKeywordsFromRanks;
      })(),
    };

    // selectedSubTab에 따라 다른 requestBody 구성
    let requestBody;

    switch (selectedSubTab) {
      case 1: // ShoppingVisitPage (쇼핑 방문)
        requestBody = {
          ...commonFields,
          // 상품 정보
          productUrl: formData.productUrl,
          // 미션 중에 하루 미션 완료 한도 있는 키
          needUpdata: true,

          // detail (쇼핑 크롤링)
          detail: 1,
          // contents 추가
          ...(formData.contents && { contents: formData.contents }),
          // channelUid 추가
          ...(formData.channelUid && { channelUid: formData.channelUid }),
        };
        break;

      case 2: // ShoppingVisitPageWithOrigin (원산지 포함)
        requestBody = {
          ...commonFields,
          // 상품 정보
          productUrl: formData.productUrl,
          needUpdata: true,
          // detail (쇼핑 크롤링)
          detail: 1,
          // contents 추가
          ...(formData.contents && { contents: formData.contents }),
          // channelUid 추가
          ...(formData.channelUid && { channelUid: formData.channelUid }),
        };
        break;

      case 3: // PlaceVisitPage (플레이스 방문)
        requestBody = {
          ...commonFields,
          // 상품 정보
          productUrl: formData.productUrl,
          // 플레이스 정보
          needUpdata: true,
          // detail (플레이스 )
          detail: 2,
        };
        break;

      case 4: // PlaceSavePage (플레이스 저장)
        requestBody = {
          ...commonFields,
          // 상품 정보
          productUrl: formData.productUrl,
          // 플레이스 정보
          needUpdata: true,
          // detail (플레이스 API)
          detail: 2,
        };
        break;

      case 5: // AppReviewPage (앱 리뷰)
        requestBody = {
          ...commonFields,
          // 상품 정보
          productUrl: `${formData.googlePlayUrl}`,
          todayRemainingCount: formData.totalQuantity,
          dailyQuantity: formData.totalQuantity,
          totalQuantity: formData.totalQuantity,
          needUpdata: false,
          endDate: (() => {
            if (formData.adEndDate) {
              return formData.adEndDate;
            } else if (formData.adStartDate) {
              const startDate = new Date(formData.adStartDate);
              const oneYearLater = new Date(startDate);
              oneYearLater.setFullYear(startDate.getFullYear() + 1);
              return oneYearLater.toISOString().slice(0, 10);
            }
            return "";
          })(),
          // detail (앱 리뷰)
          detail: 0,
        };
        break;

      case 6: // AppReviewIosPage (ios 앱 리뷰)
        requestBody = {
          ...commonFields,
          // 상품 정보
          productUrl: `${formData.appStoreUrl}`,
          todayRemainingCount: formData.totalQuantity,
          dailyQuantity: formData.totalQuantity,
          totalQuantity: formData.totalQuantity,
          needUpdata: false,
          endDate: (() => {
            if (formData.adEndDate) {
              return formData.adEndDate;
            } else if (formData.adStartDate) {
              const startDate = new Date(formData.adStartDate);
              const oneYearLater = new Date(startDate);
              oneYearLater.setFullYear(startDate.getFullYear() + 1);
              return oneYearLater.toISOString().slice(0, 10);
            }
            return "";
          })(),
          // detail (앱 리뷰)
          detail: 0,
        };
        break;
            case 13: // CoupangVisitPage (쿠팡 방문)
        requestBody = {
          ...commonFields,
          // 쿠팡 상품 정보 API 응답으로 덮어씀
          storeName: coupangProductInfo?.storeName ?? formData.storeName,
          missionName: coupangProductInfo?.productName ?? (formData.missionName || ""),
          imageUrl: coupangProductInfo?.imageUrl ?? formData.imageUrl,
          productUrl: coupangProductInfo?.originalUrl ?? formData.productUrl,
          // 미션 구동 방식 (고정값: "퀴즈")
          productName: "퀴즈",
          needUpdata: false,
          detail: 1,
          // 리뷰는 요청 최상위 contents 배열로 전달 (네이버 광고와 동일)
          contents: coupangProductInfo?.reviews ?? [],
          // memo: originPrice만 API price로 (contents는 memo 안에 넣지 않음)
          memo: (() => {
            try {
              const existingMemo = formData.memo ? JSON.parse(formData.memo) : {};
              return JSON.stringify({
                ...existingMemo,
                unitPrice: String(formData.userPriceByOne || 0),
                originProductName: existingMemo.originProductName || "",
                originPrice: coupangProductInfo?.price ?? existingMemo.originPrice ?? "",
                adTag: existingMemo.adTag || "",
              });
            } catch (e) {
              return JSON.stringify({
                unitPrice: String(formData.userPriceByOne || 0),
                originProductName: "",
                originPrice: coupangProductInfo?.price ?? "",
                adTag: "",
              });
            }
          })(),
          ...(formData.channelUid && { channelUid: formData.channelUid }),
        };
        break;


      default:
        console.error("알 수 없는 selectedSubTab:", selectedSubTab);
        setLoading(false);
        return;
    }

    try {
      // 실제 API 호출
      const response = await createMissionAPI(requestBody);

      // 성공 응답: ad_id가 있으면 성공
      if (response.data?.ad_id) {
        // 어드민들에게 알림 보내기
        try {
          if (
            zoomSettings?.adminNotification &&
            Array.isArray(zoomSettings.adminNotification)
          ) {
            const totalQuantity =
              (parseInt(formData.dailyQuantity) || 0) *
              (parseInt(formData.adDuration) || 0);
            const title = "광고 등록";
            const content = `${formData.missionName} 총 ${totalQuantity}`;
            const imageUrl = formData.imageUrl;

            // 각 어드민 ID에 대해 알림 전송
            const notificationPromises = zoomSettings.adminNotification.map(
              async (adminId) => {
                try {
                  const notificationResponse = await createAdminNotificationAPI(
                    adminId, // toUserId
                    title,
                    content,
                    imageUrl
                  );

                  return { adminId, success: true };
                } catch (error) {
                  console.error(`어드민 ${adminId} 알림 전송 실패:`, error);
                  console.error(`에러 상세:`, error.response?.data);
                  return { adminId, success: false, error: error.message };
                }
              }
            );

            const notificationResults = await Promise.allSettled(
              notificationPromises
            );

            const successCount = notificationResults.filter(
              (result) => result.status === "fulfilled" && result.value.success
            ).length;
          } else {
            console.warn(
              "adminNotification 설정이 없거나 배열이 아닙니다:",
              zoomSettings?.adminNotification
            );
          }
        } catch (notificationError) {
          console.error("어드민 알림 전송 중 오류:", notificationError);
          // 알림 전송 실패해도 광고 등록은 성공했으므로 계속 진행
        }

        alert("광고가 성공적으로 등록되었습니다.");

        // 3번째 화면으로 이동
        setCurrentStep(3);
      } else {
        // 실패 응답: msg가 있으면 실패
        const errorMessage = response.data?.msg || "알 수 없는 오류";
        console.error("광고 등록 실패:", response.data);
        alert(`광고 등록에 실패했습니다: ${errorMessage}`);
      }
    } catch (error) {
      console.error("광고 등록 API 오류:", error);
      
      // 400 에러이고 msg가 있는 경우
      if (error.response?.status === 400 && error.response?.data?.msg) {
        alert(error.response.data.msg);
      } else {
        alert(
          `광고 등록 중 오류가 발생했습니다: ${
            error.response?.data?.message || error.response?.data?.msg || error.message
          }`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExcelOrder = () => {
    console.log("엑셀로 주문하기 클릭됨");
  };

  const handleResetToStart = () => {
    // formData 초기화
    setFormData({
      idempotencyKey: "",
      serviceName: "",
      actionName: "",
      missionName: "",
      missionType: 0,
      missionTemplate: 0,
      openType: "appPush",
      openWebSite: "naver",
      rewardSilver: 0,
      webMemberId: user?.id || 0,
      productName: "",
      storeName: "",
      productUrl: "",
      productId: 0,
      originId: "",
      imageUrl: "",
      channelUid: "",
      status: "PENDING",
      memo: "",
      approveAnswer: "",
      mustRemainingTime: 0,
      detail: 0,
      needUpdata: false,
      price: 0,
      userPriceByOne: 0,
      totalQuantity: "",
      dailyQuantity: "",
      todayUseCount: 0,
      todayWaitingCount: 0,
      todayRemainingCount: 0,
      startDate: "",
      endDate: "",
      adEndDate: "",
      mainKeyword: {
        name: "",
        rank: 999,
      },
      subKeywords: [],
      placeName: "",
      placeUrl: "",
      appName: "",
      googlePlayUrl: "",
      appStoreUrl: "",
      adDuration: "",
      adStartDate: "",
      applicationQuantity: "",
      pastOrder: "",
      priceComparison: false,
    });

    // keywordRanks 초기화
    setKeywordRanks([]);

    // step을 1로 변경
    setCurrentStep(1);

    // 첫 번째 서비스와 액션 다시 로드
    if (user?.id) {
      loadMissionServices();
    }
  };

  // 모바일 화면 감지
  const isMobile = window.innerWidth <= 768;

  // 로그인되지 않은 경우 목데이터
  const mockMainTabs = [
    { id: "naver", label: "네이버 광고", active: selectedMainTab === "naver" },
    { id: "review", label: "리뷰 광고", active: selectedMainTab === "review" },
    { id: "sns", label: "SNS(출시예정)", active: selectedMainTab === "sns" },
    {
      id: "other",
      label: "기타광고(출시예정)",
      active: selectedMainTab === "other",
    },
  ];

  const mockSubTabs = {
    naver: [
      {
        id: "shopping",
        label: "쇼핑 방문",
        active: selectedSubTab === "shopping",
      },
      {
        id: "place",
        label: "플레이스 방문",
        active: selectedSubTab === "place",
      },
      {
        id: "save",
        label: "플레이스 저장",
        active: selectedSubTab === "save",
      },
    ],
    review: [
      { id: "app", label: "앱 리뷰", active: selectedSubTab === "app" },
      {
        id: "receipt",
        label: "영수증 리뷰(출시예정)",
        active: selectedSubTab === "receipt",
      },
      {
        id: "blog",
        label: "블로그 리뷰(출시예정)",
        active: selectedSubTab === "blog",
      },
    ],
  };

  // 실제 데이터 또는 목데이터 사용
  const mainTabs = user?.id
    ? missionServices.map((service) => ({
        id: service.id,
        label: service.name,
        active: selectedMainTab === service.id,
      }))
    : mockMainTabs;

  const getSubTabs = () => {
    if (!user?.id) {
      return mockSubTabs[selectedMainTab] || [];
    }

    return missionActions.map((action) => ({
      id: action.id,
      label: action.name,
      active: selectedSubTab === action.id,
    }));
  };

  // 액션별 페이지 렌더링
  const renderActionPage = () => {
    console.log("selectedMainTab", selectedMainTab);
    console.log("selectedSubTab", selectedSubTab);
    // 로그인되지 않은 경우 목데이터 기반
    if (!user?.id) {
      if (selectedMainTab === "naver") {
        if (selectedSubTab === "shopping") {
          return (
            <ShoppingVisitPage
              formData={formData}
              onInputChange={handleInputChange}
              user={user}
              onNext={handleNext}
              endSubmitTime={endSubmitTime}
              minStartDate={getMinStartDate()}
              step={currentStep}
            />
          );
        } else if (selectedSubTab === "place") {
          return (
            <PlaceVisitPage
              formData={formData}
              onInputChange={handleInputChange}
              user={user}
              onNext={handleNext}
              endSubmitTime={endSubmitTime}
              minStartDate={getMinStartDate()}
            />
          );
        } else if (selectedSubTab === "save") {
          return (
            <PlaceSavePage
              formData={formData}
              onInputChange={handleInputChange}
              user={user}
              onNext={handleNext}
              endSubmitTime={endSubmitTime}
              minStartDate={getMinStartDate()}
            />
          );
        }
      } else if (selectedMainTab === "review") {
        if (selectedSubTab === "app") {
          return (
            <AppReviewPage
              formData={formData}
              onInputChange={handleInputChange}
              user={user}
              onNext={handleNext}
              endSubmitTime={endSubmitTime}
              minStartDate={getMinStartDate()}
            />
          );
        }
      }
    } else {
      // 로그인된 경우 실제 데이터 기반
      // action.id가 1인 경우 쇼핑 방문 페이지
      if (selectedSubTab === 1) {
        return (
          <ShoppingVisitPage
            formData={formData}
            onInputChange={handleInputChange}
            user={user}
            onNext={handleNext}
            endSubmitTime={endSubmitTime}
            minStartDate={getMinStartDate()}
            step={currentStep}
            keywordRanks={keywordRanks}
            addKeywordRank={addKeywordRank}
            removeKeywordRank={removeKeywordRank}
            updateKeywordRank={updateKeywordRank}
            onKeywordRanksChange={setKeywordRanks}
            zoomSettings={zoomSettings}
          />
        );
      }
      // action.id가 2인 경우 쇼핑 방문 페이지 (원산지 포함)
      else if (selectedSubTab === 2) {
        return (
          <ShoppingVisitPageWithOrigin
            formData={formData}
            onInputChange={handleInputChange}
            user={user}
            onNext={handleNext}
            endSubmitTime={endSubmitTime}
            minStartDate={getMinStartDate()}
            step={currentStep}
            keywordRanks={keywordRanks}
            addKeywordRank={addKeywordRank}
            removeKeywordRank={removeKeywordRank}
            updateKeywordRank={updateKeywordRank}
            onKeywordRanksChange={setKeywordRanks}
            zoomSettings={zoomSettings}
          />
        );
      }
      // action.id가 3인 경우 플레이스 방문 페이지
      else if (selectedSubTab === 3) {
        return (
          <PlaceVisitPage
            formData={formData}
            onInputChange={handleInputChange}
            user={user}
            onNext={handleNext}
            endSubmitTime={endSubmitTime}
            minStartDate={getMinStartDate()}
            step={currentStep}
            keywordRanks={keywordRanks}
            addKeywordRank={addKeywordRank}
            removeKeywordRank={removeKeywordRank}
            updateKeywordRank={updateKeywordRank}
            zoomSettings={zoomSettings}
          />
        );
      }
      // action.id가 4인 경우 플레이스 저장 페이지
      else if (selectedSubTab === 4) {
        return (
          <PlaceSavePage
            formData={formData}
            onInputChange={handleInputChange}
            user={user}
            onNext={handleNext}
            endSubmitTime={endSubmitTime}
            minStartDate={getMinStartDate()}
            step={currentStep}
            keywordRanks={keywordRanks}
            addKeywordRank={addKeywordRank}
            removeKeywordRank={removeKeywordRank}
            updateKeywordRank={updateKeywordRank}
            zoomSettings={zoomSettings}
          />
        );
      }
      // action.id가 5인 경우 앱 리뷰 페이지
      else if (selectedSubTab === 5) {
        return (
          <AppReviewPage
            formData={formData}
            onInputChange={handleInputChange}
            user={user}
            onNext={handleNext}
            endSubmitTime={endSubmitTime}
            minStartDate={getMinStartDate()}
            step={currentStep}
            keywordRanks={keywordRanks}
            addKeywordRank={addKeywordRank}
            removeKeywordRank={removeKeywordRank}
            updateKeywordRank={updateKeywordRank}
            zoomSettings={zoomSettings}
          />
        );
      }

      // action.id가 5인 경우 앱 리뷰 페이지
      else if (selectedSubTab === 6) {
        return (
          <AppReviewIosPage
            formData={formData}
            onInputChange={handleInputChange}
            user={user}
            onNext={handleNext}
            endSubmitTime={endSubmitTime}
            minStartDate={getMinStartDate()}
            step={currentStep}
            keywordRanks={keywordRanks}
            addKeywordRank={addKeywordRank}
            removeKeywordRank={removeKeywordRank}
            updateKeywordRank={updateKeywordRank}
            zoomSettings={zoomSettings}
          />
        );
      }
            // action.id가 13인 경우 쿠팡 방문 페이지
      else if (selectedSubTab === 13) {
        return (
          <CoupangVisitPage
            formData={formData}
            onInputChange={handleInputChange}
            user={user}
            onNext={handleNext}
            endSubmitTime={endSubmitTime}
            minStartDate={getMinStartDate()}
            step={currentStep}
            keywordRanks={keywordRanks}
            addKeywordRank={addKeywordRank}
            removeKeywordRank={removeKeywordRank}
            updateKeywordRank={updateKeywordRank}
            onKeywordRanksChange={setKeywordRanks}
            zoomSettings={zoomSettings}
          />
        );
      }
    }

    return <div>선택된 액션이 없습니다.</div>;
  };

  return (
    <div className="addAdvertisement-widget-card">
      <LoadingOverlay isVisible={loading} message="처리 중..." />
      {currentStep === 1 ? (
        // 첫 번째 화면 (원래 구조 그대로)
        <>
          {/* 헤더 */}
          <div className="add-ad-header">
            <div className="add-header-left">
              <h1 className="add-page-title" style={{ color: COLORS.blue500 }}>
                <img
                  src={advertismentIconActive}
                  alt="광고 등록"
                  className="add-title-icon"
                />
                광고 등록
              </h1>
              <div className="deadline-info">
                <div className="deadline-time">
                  주문 마감 시간 평일 {endSubmitTime}
                </div>
                <div className="deadline-description">
                  주말에 등록하시면 월요일 관리자 확인 후 그 다음날부터 등록이
                  됩니다.
                </div>
              </div>
            </div>
            {!isMobile && (
              <button
                className="add-excel-order-btn"
                onClick={handleExcelOrder}
              >
                엑셀로 주문하기
              </button>
            )}
          </div>

          {/* 탭 컨테이너 */}
          <div className="add-tabs-container">
            {/* 메인 탭 */}
            <div className="add-main-tabs">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`add-main-tab ${tab.active ? "active" : ""}`}
                  onClick={() =>
                    user?.id
                      ? handleMainTabChange(tab.id)
                      : setSelectedMainTab(tab.id)
                  }
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* 서브 탭 */}
            {getSubTabs().length > 0 && (
              <>
                <div className="add-tabs-divider"></div>
                <div className="add-sub-tabs">
                  {getSubTabs().map((tab) => (
                    <button
                      key={tab.id}
                      className={`add-sub-tab ${tab.active ? "active" : ""}`}
                      onClick={() =>
                        user?.id
                          ? handleSubTabChange(tab.id)
                          : setSelectedSubTab(tab.id)
                      }
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}

          {/* 폼 */}
          <div className="ad-form">
            {/* 과거 주문 불러오기 */}
            <div className="add-form-section">
              <label>과거 주문 불러오기</label>
              <div className="add-custom-select-container">
                <select
                  // menuPlacement="bottom"
                  value={formData.pastOrder}
                  onChange={(e) =>
                    handleInputChange("pastOrder", e.target.value)
                  }
                  onFocus={async () => {
                    // 드롭박스 클릭 시마다 과거 주문 로드
                    if (user?.id) {
                      const selectedService = missionServices.find(
                        (service) => service.id === selectedMainTab
                      );
                      const selectedAction = missionActions.find(
                        (action) => action.id === selectedSubTab
                      );

                      if (selectedService && selectedAction) {
                        await loadPastOrders(
                          selectedService.ms_id,
                          selectedAction.ma_id
                        );
                      }
                    }
                  }}
                  className="add-past-order-select"
                  disabled={!user?.id}
                >
                  <option value="">선택</option>
                  {user?.id &&
                    pastOrders.map((order) => (
                      <option key={order.ad_id} value={order.ad_id}>
                        {order.name}
                      </option>
                    ))}
                  {!user?.id && (
                    <>
                      <option value="order1">주문 1</option>
                      <option value="order2">주문 2</option>
                      <option value="order3">주문 3</option>
                    </>
                  )}
                </select>
              </div>
            </div>

                        {/* 폼 필드 및 네비게이션 컨테이너 */}
            <div className="add-form-content-wrapper">
              {/* 동적 폼 필드들 */}
              <div className="add-form-fields">{renderActionPage()}</div>

              {/* 다음 버튼 또는 주문하기 버튼 */}
              <div className="add-step-navigation">
                {selectedSubTab === 13 ? (
                  <button
                    className="add-order-btn"
                    onClick={handleSubmit}
                    disabled={!selectedMainTab || !selectedSubTab}
                  >
                    {user?.id ? "주문하기" : "로그인하기"}
                  </button>
                ) : (
                  <button
                    className="add-next-btn"
                    onClick={handleNext}
                    disabled={!selectedMainTab || !selectedSubTab}
                  >
                    {user?.id ? "다음" : "로그인하기"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      ) : currentStep === 2 ? (
        // 두 번째 화면 - 각 페이지 컴포넌트가 자신의 두 번째 화면을 렌더링
        <>
          {/* 헤더 */}
          <div className="add-ad-header">
            <div className="add-header-left">
              <h1 className="add-page-title" style={{ color: COLORS.blue500 }}>
                <img
                  src={advertismentIconActive}
                  alt="광고 등록"
                  className="add-title-icon"
                />
                광고 등록 - 상세 입력
              </h1>
              <div className="deadline-info">
                <div className="deadline-time">
                  주문 마감 시간 평일 {endSubmitTime}
                </div>
                <div className="deadline-description">
                  주말에 등록하시면 월요일 관리자 확인 후 그 다음날부터 등록이
                  됩니다.
                </div>
              </div>
            </div>
            {!isMobile && (
              <button
                className="add-excel-order-btn"
                onClick={handleExcelOrder}
              >
                엑셀로 주문하기
              </button>
            )}
          </div>

          {/* 각 페이지 컴포넌트의 두 번째 화면 렌더링 */}
          <div className="ad-form">
            {/* 폼 필드 및 네비게이션 컨테이너 */}
            <div className="add-form-content-wrapper add-form-content-wrapper-step2">
              <div className="add-form-fields">{renderActionPage()}</div>

              {/* 액션 버튼들 */}
              <div className="add-step-navigation">
                {/* <button className="add-back-btn" onClick={handleBack}>
              돌아가기
            </button> */}
                <button
                  className="add-order-btn"
                  onClick={handleSubmit}
                  disabled={
                    formData.productName === "복합" &&
                    !validateCompositeQuantity().isValid
                  }
                >
                  주문하기
                </button>
              </div>
            </div>
          </div>
        </>
      ) : currentStep === 3 ? (
        // 세 번째 화면 - 광고 등록 완료
        <>
          {/* 헤더 */}
          <div className="add-ad-header">
            <div className="add-header-left">
              <h1 className="add-page-title" style={{ color: COLORS.blue500 }}>
                <img
                  src={advertismentIconActive}
                  alt="광고 등록"
                  className="add-title-icon"
                />
                광고 등록
              </h1>
            </div>
          </div>

          {/* 완료 화면 - 이미지와 동일한 디자인 */}
          <div className="completion-container">
            <div className="completion-content">
              <div className="success-checkmark">✅</div>
              <h2 className="completion-title">광고 등록이 완료되었습니다.</h2>
              <button className="add-reset-btn" onClick={handleResetToStart}>
                처음으로
              </button>
            </div>
          </div>
        </>
      ) : null}

      {/* 쿠팡 방문 경고 확인 모달 */}
      {showConfirmModal && (
        <div
          className="coupang-confirm-modal-overlay"
          onClick={handleCancelSubmit}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
        >
          <div
            className="coupang-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "32px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            }}
          >
            <div
              style={{
                textAlign: "center",
                marginBottom: "24px",
              }}
            >
              <div
                style={{
                  fontSize: "48px",
                  marginBottom: "16px",
                }}
              >
                ⚠️
              </div>
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  margin: "0 0 12px 0",
                  color: "#1f2937",
                }}
              >
                주문 확인
              </h3>
              <p
                style={{
                  fontSize: "15px",
                  color: "#6b7280",
                  lineHeight: "1.6",
                  margin: 0,
                }}
              >
                해당 상품은 중단 환불이 불가능합니다.
                <br />
                정말 주문하시겠습니까?
              </p>
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                onClick={handleCancelSubmit}
                style={{
                  padding: "12px 24px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  background: "white",
                  color: "#374151",
                  fontSize: "15px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#f9fafb";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "white";
                }}
              >
                취소
              </button>
              <button
                onClick={handleConfirmSubmit}
                style={{
                  padding: "12px 24px",
                  border: "none",
                  borderRadius: "8px",
                  background: "#2563eb",
                  color: "white",
                  fontSize: "15px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#1d4ed8";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "#2563eb";
                }}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddAdvertisement;
